/**
 * API Service for making HTTP requests
 */

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

export const AUTH_SESSION_EXPIRED_EVENT = 'auth:session-expired';

/** Seconds before token exp we consider it expired (avoids edge cases from clock skew / latency). */
const TOKEN_EXPIRY_BUFFER_SECONDS = 60;

function getJwtExpiration(token) {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    let payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    while (payload.length % 4) payload += '=';
    const decoded = JSON.parse(atob(payload));
    return typeof decoded.exp === 'number' ? decoded.exp : null;
  } catch (e) {
    return null;
  }
}

/**
 * Returns true if the token is missing, invalid, or expired (within TOKEN_EXPIRY_BUFFER_SECONDS of exp).
 */
export function isAccessTokenExpired(token) {
  if (!token) return true;
  const exp = getJwtExpiration(token);
  if (exp == null) return true;
  return exp <= Date.now() / 1000 + TOKEN_EXPIRY_BUFFER_SECONDS;
}

/**
 * Removes the access token from localStorage if it is expired. Idempotent.
 * @returns {boolean} true if a token was removed, false otherwise
 */
export function clearExpiredAccessToken() {
  const token = localStorage.getItem('access_token');
  if (!token) return false;
  if (!isAccessTokenExpired(token)) return false;
  localStorage.removeItem('access_token');
  return true;
}

/**
 * Returns true only if there is a valid (non-expired) access token in localStorage.
 */
export function isAccessTokenValid() {
  const token = localStorage.getItem('access_token');
  if (!token) return false;
  return !isAccessTokenExpired(token);
}

const getAuthHeaders = () => {
  const headers = {};
  const token = localStorage.getItem('access_token');
  if (token) headers['X-Access-Token'] = token;
  const activeOrgId = localStorage.getItem('active_org_id');
  if (activeOrgId) headers['X-Active-Org-Id'] = activeOrgId;
  return headers;
};

// Enhanced logging utility.
//
// Silent in production builds. These helpers print request bodies and response
// payloads, which for the document field review endpoints carry extracted and
// human-corrected values lifted off scanned documents. The console is only
// visible to the already-authenticated user, but it is still an avoidable
// surface: browser extensions, error-reporting and session-replay tooling, and
// support screenshares all pick console output up. The backend deliberately
// keeps field values out of its own logs; this matches that posture.
// Request/response logging is silent in production builds — the console is only an
// avoidable surface in dev/staging (screen shares, extensions, "paste the console"
// support asks). Errors still log (redacted) in production because losing them there
// would blind support. (PRD-1302)
const LOGGING_ENABLED = process.env.NODE_ENV !== 'production';

// Header keys whose VALUE is a credential — never print it, in any environment.
const SENSITIVE_HEADER_KEYS = ['X-Access-Token', 'Authorization', 'X-Api-Key'];

// Body/response keys that carry recipient or contact data (customer PII) — never print
// the value. Matched case-insensitively and by substring so future recipient/contact
// fields (reportRecipients, notification_emails, ccEmails, ...) are covered too. (PRD-1302)
// Key matching is a FIRST pass only. Key names drift (sendTo, send_to, mailTo,
// resapi's 422 detail[].input) and short tokens like "to"/"cc" can't be matched by
// substring without also hitting "total"/"account", so the value-based email scrub
// below is the real backstop (review on #198).
const SENSITIVE_BODY_KEY_PATTERNS = ['recipient', 'mail', 'contact', 'phone'];
const _sensitiveBodyKeyRe = new RegExp(SENSITIVE_BODY_KEY_PATTERNS.join('|'), 'i');

const REDACTED = '<redacted>';

// Email-shaped substrings are redacted wherever they appear — any key, any nesting,
// the 422 detail[].input value, an error message — so a recipient address is never
// printed even when the key name doesn't look sensitive (review on #198).
const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
const redactEmails = (value) => (typeof value === 'string' ? value.replace(EMAIL_RE, REDACTED) : value);

// X-Access-Token is the user's JWT — never print it, in any environment.
const redactHeaders = (headers) => {
  if (!headers || typeof headers !== 'object') return headers;
  const safe = { ...headers };
  for (const key of Object.keys(safe)) {
    if (SENSITIVE_HEADER_KEYS.some((h) => h.toLowerCase() === key.toLowerCase())) safe[key] = REDACTED;
  }
  return safe;
};

// Return a copy of a request/response body with recipient/contact/credential values
// replaced by <redacted>, recursively. A value is dropped wholesale when its KEY looks
// like recipient/contact data; every string value (any key, any depth) is additionally
// scrubbed of email addresses. Numbers/booleans pass through. (PRD-1302)
const redactBody = (body) => {
  if (typeof body === 'string') return redactEmails(body);
  if (Array.isArray(body)) return body.map(redactBody);
  if (!body || typeof body !== 'object') return body;
  const safe = {};
  for (const [key, value] of Object.entries(body)) {
    safe[key] = _sensitiveBodyKeyRe.test(key) ? REDACTED : redactBody(value);
  }
  return safe;
};

// Redact any argument passed to the debug helpers. An Error's own fields aren't
// enumerable, so redactBody would flatten it to {} and swallow it in dev — pass its
// email-scrubbed message through instead. Everything else (objects, strings, numbers)
// flows through redactBody, which scrubs email-shaped strings. (review on #198)
const redactArg = (arg) => (arg instanceof Error ? redactEmails(arg.message) : redactBody(arg));

// Gated, redacted stand-ins for the ad-hoc console.log/console.error calls scattered
// through this file, so no request/response detail reaches the production console and
// no call site has to remember to redact. (PRD-1302)
const logDebug = (...args) => {
  if (!LOGGING_ENABLED) return;
  console.log(...args.map(redactArg));
};

const logDebugError = (...args) => {
  if (!LOGGING_ENABLED) return;
  console.error(...args.map(redactArg));
};

const logDebugWarn = (...args) => {
  if (!LOGGING_ENABLED) return;
  console.warn(...args.map(redactArg));
};

// Genuine error/warning signals that must survive into production so support keeps a
// trail (an SSE failure, a degraded-config fallback). Always redacted, never gated —
// the opposite trade-off from logDebug*, which is dev-only request/response tracing.
// (review on #198, Uthika)
const logGenuineError = (...args) => console.error(...args.map(redactArg));
const logGenuineWarn = (...args) => console.warn(...args.map(redactArg));

const logRequest = (method, url, headers, body) => {
  if (!LOGGING_ENABLED) return;
  console.group(`[API Service] ${method} ${url}`);
  console.log('Headers:', redactHeaders(headers));
  console.log('Body:', redactBody(body));
  console.log('Full URL:', url);
  console.groupEnd();
};

const logResponse = (response, data) => {
  if (!LOGGING_ENABLED) return;
  console.group(`[API Service] Response`);
  console.log('Status:', response.status, response.statusText);
  console.log('Headers:', redactHeaders(Object.fromEntries(response.headers.entries())));
  console.log('Data:', redactBody(data));
  console.groupEnd();
};

// Errors still log in production (redacted) so support keeps a signal — but never the
// token or recipient/contact values. The Error object itself carries no headers here
// (buildAndThrowApiError sets only .status/.responseData), and its body is redacted.
const logError = (error, context) => {
  console.group(`[API Service] Error - ${context}`);
  // buildAndThrowApiError builds .message from the response detail/msg, which can carry
  // a recipient address (e.g. "Invalid recipient a@b.com") — scrub it, and the stack,
  // before printing. This runs in prod, so the redaction is not optional (review on #198).
  console.error('Error message:', redactEmails(error && error.message));
  if (LOGGING_ENABLED) console.error('Error stack:', redactEmails(error && error.stack));
  if (error && error.status) console.error('Response status:', error.status);
  if (error && error.responseData) console.error('Response data:', redactBody(error.responseData));
  if (error && error.response) {
    console.error('Response status:', error.response.status);
    console.error('Response data:', redactBody(error.response.data));
  }
  console.groupEnd();
};

// Exported for unit tests (PRD-1302). Runtime callers use the closures above.
export { redactHeaders, redactBody, redactArg, logRequest, logResponse, logError, logGenuineError };

/** Build error message from API response and throw. Use after !response.ok. */
const buildAndThrowApiError = (response, responseData, defaultMessage, context) => {
  let errorDetail = defaultMessage;
  if (responseData) {
    if (Array.isArray(responseData.detail)) {
      errorDetail = responseData.detail.map(err =>
        `${err.loc?.join('.')}: ${err.msg}`
      ).join(', ');
    } else if (typeof responseData.detail === 'string') {
      errorDetail = responseData.detail;
    } else if (typeof responseData.detail?.message === 'string') {
      // A coded refusal: `{ code, message }`. The code is carried on the error below.
      errorDetail = responseData.detail.message;
    } else if (responseData.message) {
      errorDetail = responseData.message;
    }
  }
  if (response.status === 422) {
    errorDetail = `Validation error (422): ${errorDetail}`;
  } else if (response.status === 500) {
    errorDetail = `Server error (500): ${errorDetail}`;
  } else if (response.status === 404) {
    errorDetail = `Not found (404): ${errorDetail}`;
  } else if (response.status === 400) {
    errorDetail = `Bad request (400): ${errorDetail}`;
  } else if (response.status === 401) {
    localStorage.removeItem('access_token');
    window.dispatchEvent(new CustomEvent(AUTH_SESSION_EXPIRED_EVENT));
    errorDetail = responseData?.detail || 'Session expired. Please log in again.';
  }
  const error = new Error(errorDetail);
  error.status = response.status;
  error.responseData = responseData;
  if (typeof responseData?.detail?.code === 'string') error.code = responseData.detail.code;
  logError(error, context);
  throw error;
};

/** If error is a network/fetch error, log and throw a friendly error; otherwise no-op. */
const throwIfNetworkError = (error, context) => {
  if (error.name === 'TypeError' && error.message.includes('fetch')) {
    const networkError = new Error(
      `Network error: Unable to connect to ${API_BASE_URL}. ` +
      `Please check if the server is running and CORS is configured. ` +
      `Original error: ${error.message}`
    );
    logError(networkError, `${context} - Network Error`);
    throw networkError;
  }
};

/** Default supported file types when config API is unavailable (must match backend config). */
export const DEFAULT_SUPPORTED_FILE_TYPES = {
  accept: '.pdf,.csv,.xlsx,.wav,.mp3',
  labels: 'PDF, CSV, XLSX, WAV, MP3',
};

// A widget query that fails at the HTTP layer still has a KIND. Without the status
// the hook can only call everything a network error, so a 403 -- "you do not have
// access to one or more selected datasets" -- reached the card as "couldn't load",
// beside a Retry button that could never succeed. The card showed the real sentence
// while the notification said something else, because only the text survived the
// throw and the classification did not.
function widgetQueryError(message, status) {
  const err = new Error(message);
  err.status = status;
  return err;
}

export const apiService = {
  /**
   * Get supported file types for data onboarding (from backend config).
   */
  async getSupportedFileTypes() {
    const url = `${API_BASE_URL}/api/v1/config/supported-file-types`;
    const headers = { ...getAuthHeaders() };
    try {
      const response = await fetch(url, { headers });
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          logGenuineWarn('[API Service] getSupportedFileTypes: auth failed (401/403), using defaults. User may see stale file type config.');
        } else {
          logGenuineWarn('[API Service] getSupportedFileTypes: request failed', response.status, response.statusText, ', using defaults.');
        }
        return DEFAULT_SUPPORTED_FILE_TYPES;
      }
      const data = await response.json();
      return {
        accept: data.accept || DEFAULT_SUPPORTED_FILE_TYPES.accept,
        extensions: data.extensions || [],
        labels: data.labels || DEFAULT_SUPPORTED_FILE_TYPES.labels,
      };
    } catch (e) {
      const isNetwork = e?.name === 'TypeError' || e?.message?.includes('fetch');
      logGenuineWarn(
        '[API Service] getSupportedFileTypes failed, using defaults.',
        isNetwork ? 'Network or CORS error - user may be offline or see stale file type config.' : e
      );
      return DEFAULT_SUPPORTED_FILE_TYPES;
    }
  },

  /**
   * Create a new dataset
   * @param {Object} data - { name, description, prompt, domain }
   * @returns {Promise<Object>} Dataset response with id
   */
  async createDataset(data) {
    const url = `${API_BASE_URL}/api/v1/datasets`;
    const headers = {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    };

    logRequest('POST', url, headers, data);

    try {
      logDebug('[API Service] Making fetch request...');
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
      });

      logDebug('[API Service] Response received:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        headers: Object.fromEntries(response.headers.entries())
      });

      let responseData;
      const contentType = response.headers.get('content-type');

      if (contentType && contentType.includes('application/json')) {
        try {
          responseData = await response.json();
          logResponse(response, responseData);
        } catch (e) {
          logDebugError('[API Service] Failed to parse JSON response:', e);
          const text = await response.text();
          logDebugError('[API Service] Response text:', text);
          responseData = { detail: text || 'Invalid JSON response' };
        }
      } else {
        const text = await response.text();
        logDebugWarn('[API Service] Non-JSON response:', text);
        responseData = { detail: text || 'Unexpected response format' };
      }

      if (!response.ok) {
        buildAndThrowApiError(response, responseData, 'Failed to create dataset', 'createDataset');
      }

      logDebug('[API Service] Success! Dataset created:', responseData);
      return responseData;
    } catch (error) {
      throwIfNetworkError(error, 'createDataset');
      logError(error, 'createDataset - General Error');
      throw error;
    }
  },

  /**
   * Upload files to a dataset
   * @param {string} datasetId - Dataset ID
   * @param {FileList} files - File list to upload (for local / chat_recording source)
   * @param {string} source - Source type: 'local', 'azure', 's3', or 'chat_recording'
   * @param {string} storageUrl - Cloud storage URL (for cloud source)
   * @param {string} token - Authentication token (for cloud source)
   * @param {Object} [metadata] - Extra form fields for in-chat recordings (PRD-941):
   *   { duration_seconds: number, recorded_at: string (ISO-8601), mime_type: string }.
   *   Creator is derived server-side from the token — never sent here.
   * @returns {Promise<Array|Object>} Array of file upload responses (201), or
   *   `{ uploaded, rejected }` when some files were rejected (207, PRD-1628) —
   *   read either with `readUploadOutcome` in utils/uploadOutcome.js
   */
  async uploadFiles(datasetId, files, source = 'local', storageUrl = '', token = '', metadata = {}) {
    const url = `${API_BASE_URL}/api/v1/datasets/${datasetId}/files`;
    const formData = new FormData();

    // Add source type
    formData.append('source', source);

    // Add Azure parameters if source is azure
    if (source === 'azure') {
      formData.append('storage_url', storageUrl);
      formData.append('token', token);
    }

    // chat_recording carries the recorded blob like a local file, plus recording metadata.
    if (source === 'chat_recording') {
      if (metadata.duration_seconds != null) formData.append('duration_seconds', String(metadata.duration_seconds));
      if (metadata.recorded_at) formData.append('recorded_at', metadata.recorded_at);
      if (metadata.mime_type) formData.append('mime_type', metadata.mime_type);
    }

    // Attach the actual file(s) for local uploads and in-chat recordings.
    let fileArray = [];
    if ((source === 'local' || source === 'chat_recording') && files) {
      fileArray = Array.from(files);
      logDebug('[API Service] Uploading files:', {
        datasetId,
        source,
        fileCount: fileArray.length,
        fileNames: fileArray.map(f => f.name),
        fileSizes: fileArray.map(f => `${(f.size / 1024).toFixed(2)} KB`)
      });

      fileArray.forEach((file) => {
        formData.append('files', file);
      });
    }

    const headers = { ...getAuthHeaders() };

    const logMessage = source === 'local' && fileArray.length > 0
      ? `FormData with ${fileArray.length} file(s)`
      : `FormData with source=${source}`;
    logRequest('POST', url, headers, logMessage);

    try {
      logDebug('[API Service] Making file upload request...');
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: formData,
      });

      logDebug('[API Service] Upload response received:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok
      });

      let responseData;
      const contentType = response.headers.get('content-type');

      if (contentType && contentType.includes('application/json')) {
        try {
          responseData = await response.json();
          logResponse(response, responseData);
        } catch (e) {
          logDebugError('[API Service] Failed to parse JSON response:', e);
          const text = await response.text();
          logDebugError('[API Service] Response text:', text);
          responseData = { detail: text || 'Invalid JSON response' };
        }
      } else {
        const text = await response.text();
        logDebugWarn('[API Service] Non-JSON response:', text);
        responseData = { detail: text || 'Unexpected response format' };
      }

      if (!response.ok) {
        buildAndThrowApiError(response, responseData, 'Failed to upload files', 'uploadFiles');
      }

      logDebug('[API Service] Files uploaded successfully:', responseData);
      return responseData;
    } catch (error) {
      throwIfNetworkError(error, 'uploadFiles');
      logError(error, 'uploadFiles - General Error');
      throw error;
    }
  },

  /**
   * Get files for a dataset
   * @param {string} datasetId - Dataset ID
   * @param {string} status - Optional status filter (uploaded, processing, complete, failed, rejected)
   * @returns {Promise<Object>} { items: DatasetFile[], total: number, dataset_id: string }
   */
  async getDatasetFiles(datasetId, status = null) {
    let url = `${API_BASE_URL}/api/v1/datasets/${datasetId}/files`;
    if (status) {
      url += `?status=${status}`;
    }

    logRequest('GET', url, {}, null);

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      });

      const responseData = await response.json();
      logResponse(response, responseData);

      if (!response.ok) {
        throw new Error(responseData.detail || 'Failed to get dataset files');
      }

      return responseData;
    } catch (error) {
      logError(error, 'getDatasetFiles');
      throw error;
    }
  },

  /**
   * Get file counts grouped by status for a dataset
   * @param {string} datasetId - Dataset ID
   * @returns {Promise<Object>} { dataset_id, total, uploaded, processing, complete, failed, rejected }
   */
  async getDatasetFilesSummary(datasetId) {
    const url = `${API_BASE_URL}/api/v1/datasets/${datasetId}/files/summary`;
    const headers = { 'Content-Type': 'application/json', ...getAuthHeaders() };

    logRequest('GET', url, headers, null);

    try {
      const response = await fetch(url, { method: 'GET', headers });
      const responseData = await response.json();
      logResponse(response, responseData);

      if (!response.ok) {
        buildAndThrowApiError(response, responseData, 'Failed to get file summary', 'getDatasetFilesSummary');
      }

      return responseData;
    } catch (error) {
      throwIfNetworkError(error, 'getDatasetFilesSummary');
      logError(error, 'getDatasetFilesSummary');
      throw error;
    }
  },

  /**
   * Process inspection forms for a dataset
   * @param {string} datasetId - Dataset ID
   * @returns {Promise<Object>} InspectionFormResponse with job_id
   */
  async processInspectionForms(datasetId) {
    const url = `${API_BASE_URL}/api/v1/datasets/${datasetId}/inspection-forms/process`;
    const headers = {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    };

    logRequest('POST', url, headers, null);

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
      });

      let responseData;
      const contentType = response.headers.get('content-type');

      if (contentType && contentType.includes('application/json')) {
        try {
          responseData = await response.json();
          logResponse(response, responseData);
        } catch (e) {
          logDebugError('[API Service] Failed to parse JSON response:', e);
          const text = await response.text();
          responseData = { detail: text || 'Invalid JSON response' };
        }
      } else {
        const text = await response.text();
        responseData = { detail: text || 'Unexpected response format' };
      }

      if (!response.ok) {
        buildAndThrowApiError(response, responseData, 'Failed to process inspection forms', 'processInspectionForms');
      }

      return responseData;
    } catch (error) {
      throwIfNetworkError(error, 'processInspectionForms');
      logError(error, 'processInspectionForms - General Error');
      throw error;
    }
  },

  /**
   * Get a single dataset by ID
   * @param {string} datasetId - Dataset ID
   * @returns {Promise<Object>} DatasetResponse
   */
  async getDataset(datasetId) {
    const url = `${API_BASE_URL}/api/v1/datasets/${datasetId}`;
    const headers = {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    };

    logRequest('GET', url, headers, null);

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers,
      });

      let responseData;
      const contentType = response.headers.get('content-type');

      if (contentType && contentType.includes('application/json')) {
        try {
          responseData = await response.json();
          logResponse(response, responseData);
        } catch (e) {
          logDebugError('[API Service] Failed to parse JSON response:', e);
          const text = await response.text();
          responseData = { detail: text || 'Invalid JSON response' };
        }
      } else {
        const text = await response.text();
        responseData = { detail: text || 'Unexpected response format' };
      }

      if (!response.ok) {
        buildAndThrowApiError(response, responseData, 'Failed to get dataset', 'getDataset');
      }

      return responseData;
    } catch (error) {
      throwIfNetworkError(error, 'getDataset');
      logError(error, 'getDataset - General Error');
      throw error;
    }
  },

  /**
   * Extract fields from dataset files
   * Checks domain and calls appropriate endpoint (inspection forms for circle/tick inspection, regular extraction otherwise)
   * @param {string} datasetId - Dataset ID
   * @returns {Promise<Object>} ExtractFieldsResponse or InspectionFormResponse with job_id
   */
  async extractFields(datasetId) {
    // First, get the dataset to check its domain
    const dataset = await this.getDataset(datasetId);

    // Check if domain is circle inspection or tick inspection
    const domain = dataset.domain;
    if (domain === 'Circle inspection' || domain === 'Tick inspection') {
      // Call inspection forms processing endpoint
      return await this.processInspectionForms(datasetId);
    }

    // Otherwise, call the regular extraction endpoint
    const url = `${API_BASE_URL}/api/v1/datasets/${datasetId}/flows/interactive`;
    const headers = {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    };

    logRequest('POST', url, headers, null);

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
      });

      let responseData;
      const contentType = response.headers.get('content-type');

      if (contentType && contentType.includes('application/json')) {
        try {
          responseData = await response.json();
          logResponse(response, responseData);
        } catch (e) {
          logDebugError('[API Service] Failed to parse JSON response:', e);
          const text = await response.text();
          responseData = { detail: text || 'Invalid JSON response' };
        }
      } else {
        const text = await response.text();
        responseData = { detail: text || 'Unexpected response format' };
      }

      if (!response.ok) {
        buildAndThrowApiError(response, responseData, 'Failed to extract fields', 'extractFields');
      }

      return responseData;
    } catch (error) {
      throwIfNetworkError(error, 'extractFields');
      logError(error, 'extractFields - General Error');
      throw error;
    }
  },

  /**
   * Validate prompt with refinement notes
   * @param {string} datasetId - Dataset ID
   * @param {string} prompt - Refined prompt
   * @returns {Promise<Object>} PromptValidationResponse
   */
  async validatePrompt(datasetId, prompt) {
    const url = `${API_BASE_URL}/api/v1/datasets/${datasetId}/validate_prompt`;
    const headers = {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    };

    const body = { prompt };

    logRequest('POST', url, headers, body);

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });

      let responseData;
      const contentType = response.headers.get('content-type');

      if (contentType && contentType.includes('application/json')) {
        responseData = await response.json();
        logResponse(response, responseData);
      } else {
        const text = await response.text();
        responseData = { detail: text || 'Unexpected response format' };
      }

      if (!response.ok) {
        buildAndThrowApiError(response, responseData, 'Failed to validate prompt', 'validatePrompt');
      }

      return responseData;
    } catch (error) {
      throwIfNetworkError(error, 'validatePrompt');
      logError(error, 'validatePrompt - General Error');
      throw error;
    }
  },

  /**
   * Update dataset status
   * @param {string} datasetId - Dataset ID
   * @param {string} status - Status to set ('draft' or 'published')
   * @returns {Promise<Object>} Updated dataset response
   */
  async updateDatasetStatus(datasetId, status) {
    const url = `${API_BASE_URL}/api/v1/datasets/${datasetId}/status`;
    const headers = {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    };

    const body = { status };

    logRequest('PATCH', url, headers, body);

    try {
      const response = await fetch(url, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(body),
      });

      let responseData;
      const contentType = response.headers.get('content-type');

      if (contentType && contentType.includes('application/json')) {
        responseData = await response.json();
        logResponse(response, responseData);
      } else {
        const text = await response.text();
        responseData = { detail: text || 'Unexpected response format' };
      }

      if (!response.ok) {
        buildAndThrowApiError(response, responseData, 'Failed to update dataset status', 'updateDatasetStatus');
      }

      logDebug('[API Service] Dataset status updated successfully, status code:', response.status);
      return responseData;
    } catch (error) {
      throwIfNetworkError(error, 'updateDatasetStatus');
      logError(error, 'updateDatasetStatus - General Error');
      throw error;
    }
  },

  /**
   * Retry failed files in a dataset (PRD-1544 / PRD-1543, gated by `document_retry`).
   * The backend dispatches on its own — do NOT call /route after this; status
   * arrives through the existing dataset/files polling.
   * @param {string} datasetId
   * @param {string[]} fileIds - the file ids to retry
   * @returns {Promise<{job_id: string, accepted: string[], rejected: Array<{file_id: string, reason_code: string}>}>}
   *   202 body. Throws with .status / .responseData on 403/404/409/422.
   */
  async retryDatasetFiles(datasetId, fileIds) {
    const url = `${API_BASE_URL}/api/v1/datasets/${datasetId}/files/retry`;
    const headers = {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    };
    const body = { file_ids: fileIds };

    logRequest('POST', url, headers, body);

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });

      let responseData;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        responseData = await response.json();
        logResponse(response, responseData);
      } else {
        const text = await response.text();
        responseData = { detail: text || 'Unexpected response format' };
      }

      if (!response.ok) {
        buildAndThrowApiError(response, responseData, 'Failed to retry files', 'retryDatasetFiles');
      }

      logDebug('[API Service] Retry accepted, status code:', response.status);
      return responseData;
    } catch (error) {
      throwIfNetworkError(error, 'retryDatasetFiles');
      logError(error, 'retryDatasetFiles - General Error');
      throw error;
    }
  },

  /**
   * Get delete preview for a dataset
   * @param {string} datasetId - Dataset ID
   * @returns {Promise<Object>} DatasetDeletePreview
   */
  // How many OTHER people's widgets un-sharing this dataset would break. Your own
  // survive, since you own the dataset, so they are not counted.
  async datasetSharingImpact(datasetId) {
    const url = `${API_BASE_URL}/api/v1/datasets/${datasetId}/sharing-impact`;
    const response = await fetch(url, { headers: { ...getAuthHeaders() } });
    const data = await response.json();
    if (!response.ok) {
      const err = new Error(data.detail || 'Failed to check sharing impact');
      err.status = response.status;
      throw err;
    }
    return data;
  },

  async deleteDatasetPreview(datasetId) {
    const url = `${API_BASE_URL}/api/v1/datasets/${datasetId}/delete-preview`;
    const headers = {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    };

    logRequest('GET', url, headers, null);

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers,
      });

      let responseData;
      const contentType = response.headers.get('content-type');

      if (contentType && contentType.includes('application/json')) {
        responseData = await response.json();
        logResponse(response, responseData);
      } else {
        const text = await response.text();
        responseData = { detail: text || 'Unexpected response format' };
      }

      if (!response.ok) {
        buildAndThrowApiError(response, responseData, 'Failed to get delete preview', 'deleteDatasetPreview');
      }

      return responseData;
    } catch (error) {
      throwIfNetworkError(error, 'deleteDatasetPreview');
      logError(error, 'deleteDatasetPreview - General Error');
      throw error;
    }
  },

  /**
   * Delete a dataset (soft-delete)
   * @param {string} datasetId - Dataset ID
   * @returns {Promise<Object>} { ok: true, message: string }
   */
  /**
   * @param {string} datasetId
   * @param {{ force?: boolean }} [opts]  force=true cancels in-flight jobs and deletes anyway.
   */
  async deleteDataset(datasetId, opts = {}) {
    const qs = opts.force ? '?force=true' : '';
    const url = `${API_BASE_URL}/api/v1/datasets/${datasetId}${qs}`;
    const headers = {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    };

    logRequest('DELETE', url, headers, null);

    try {
      const response = await fetch(url, {
        method: 'DELETE',
        headers,
      });

      let responseData;
      const contentType = response.headers.get('content-type');

      if (contentType && contentType.includes('application/json')) {
        responseData = await response.json();
        logResponse(response, responseData);
      } else {
        const text = await response.text();
        responseData = { detail: text || 'Unexpected response format' };
      }

      if (!response.ok) {
        buildAndThrowApiError(response, responseData, 'Failed to delete dataset', 'deleteDataset');
      }

      return responseData;
    } catch (error) {
      throwIfNetworkError(error, 'deleteDataset');
      logError(error, 'deleteDataset - General Error');
      throw error;
    }
  },

  /** POST /api/v1/datasets/{id}/pause — soft pause; in-flight tasks finish, new dispatch blocked. */
  async pauseDataset(datasetId) {
    const url = `${API_BASE_URL}/api/v1/datasets/${datasetId}/pause`;
    const headers = { ...getAuthHeaders() };
    logRequest('POST', url, headers, null);
    try {
      const response = await fetch(url, { method: 'POST', headers });
      const responseData = response.headers.get('content-type')?.includes('application/json')
        ? await response.json()
        : { detail: (await response.text()) || 'Unexpected response format' };
      logResponse(response, responseData);
      if (!response.ok) buildAndThrowApiError(response, responseData, 'Failed to pause dataset', 'pauseDataset');
      return responseData;
    } catch (error) {
      throwIfNetworkError(error, 'pauseDataset');
      logError(error, 'pauseDataset - General Error');
      throw error;
    }
  },

  /** POST /api/v1/datasets/{id}/resume — re-enables dispatch. */
  async resumeDataset(datasetId) {
    const url = `${API_BASE_URL}/api/v1/datasets/${datasetId}/resume`;
    const headers = { ...getAuthHeaders() };
    logRequest('POST', url, headers, null);
    try {
      const response = await fetch(url, { method: 'POST', headers });
      const responseData = response.headers.get('content-type')?.includes('application/json')
        ? await response.json()
        : { detail: (await response.text()) || 'Unexpected response format' };
      logResponse(response, responseData);
      if (!response.ok) buildAndThrowApiError(response, responseData, 'Failed to resume dataset', 'resumeDataset');
      return responseData;
    } catch (error) {
      throwIfNetworkError(error, 'resumeDataset');
      logError(error, 'resumeDataset - General Error');
      throw error;
    }
  },

  /**
   * Update dataset metadata (name, description)
   * @param {string} datasetId - Dataset ID
   * @param {Object} data - { name?: string, description?: string }
   * @returns {Promise<Object>} Updated DatasetResponse
   */
  async updateDataset(datasetId, data) {
    const url = `${API_BASE_URL}/api/v1/datasets/${datasetId}`;
    const headers = {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    };

    logRequest('PATCH', url, headers, data);

    try {
      const response = await fetch(url, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(data),
      });

      let responseData;
      const contentType = response.headers.get('content-type');

      if (contentType && contentType.includes('application/json')) {
        responseData = await response.json();
        logResponse(response, responseData);
      } else {
        const text = await response.text();
        responseData = { detail: text || 'Unexpected response format' };
      }

      if (!response.ok) {
        buildAndThrowApiError(response, responseData, 'Failed to update dataset', 'updateDataset');
      }

      return responseData;
    } catch (error) {
      throwIfNetworkError(error, 'updateDataset');
      logError(error, 'updateDataset - General Error');
      throw error;
    }
  },

  /**
   * Get list of datasets
   * @param {Object} params - Query parameters (page, page_size, status, search, sort)
   * @returns {Promise<Object>} PaginatedDatasetResponse
   */
  async getDatasets(params = {}, ifNoneMatch = null) {
    const { page = 1, page_size = 20, status, search, sort = '-created_at', module_id, scope } = params;

    const url = new URL(`${API_BASE_URL}/api/v1/datasets`);
    url.searchParams.append('page', page);
    url.searchParams.append('page_size', page_size);
    if (sort) url.searchParams.append('sort', sort);
    // Org-dataset-sharing view: 'mine' | 'shared' | 'all'. Ignored server-side
    // when the org-dataset-sharing flag is off.
    if (scope) url.searchParams.append('scope', scope);
    if (status) {
      if (Array.isArray(status)) {
        status.forEach(s => url.searchParams.append('status', s));
      } else {
        url.searchParams.append('status', status);
      }
    }
    if (search) url.searchParams.append('search', search);
    // Partition filter. Pass 'null' for working-only, a UUID for a
    // specific module. Omit to get everything (default).
    if (module_id !== undefined && module_id !== null) url.searchParams.append('module_id', module_id);
    else if (module_id === null) url.searchParams.append('module_id', 'null');

    const headers = { ...getAuthHeaders() };
    if (ifNoneMatch) headers['If-None-Match'] = ifNoneMatch;

    logRequest('GET', url.toString(), headers, null);

    try {
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers,
      });

      if (response.status === 304) {
        logResponse(response, { notModified: true });
        return { notModified: true, _etag: ifNoneMatch };
      }

      let responseData;
      const contentType = response.headers.get('content-type');

      if (contentType && contentType.includes('application/json')) {
        responseData = await response.json();
        logResponse(response, responseData);
      } else {
        const text = await response.text();
        responseData = { detail: text || 'Unexpected response format' };
      }

      if (!response.ok) {
        buildAndThrowApiError(response, responseData, 'Failed to get datasets', 'getDatasets');
      }

      return { ...responseData, _etag: response.headers.get('ETag') };
    } catch (error) {
      throwIfNetworkError(error, 'getDatasets');
      logError(error, 'getDatasets - General Error');
      throw error;
    }
  },

  /**
   * Every dataset matching `params`, walking the pages rather than trusting one.
   *
   * `/datasets` has no id filter and defaults to `-created_at`, so a caller that
   * fetched page 1 and filtered client-side silently lost everything past the
   * page boundary — the oldest datasets, which are the likeliest to be settled
   * and referenced by an existing automation. It failed quietly: no error, no
   * empty state, just a list with things missing (raised on PR #180).
   *
   * Stops on the first short page, or on `total_pages` when the server sends it.
   * MAX_PAGES bounds the walk so a pagination bug cannot turn into an unbounded
   * request loop.
   *
   * @param {object} [params] as getDatasets, minus `page`.
   * @param {(items: object[]) => boolean} [isDone] called with everything
   *   accumulated so far after each page; return true to stop early. Lets a
   *   caller that knows which ids it wants avoid fetching the whole estate —
   *   usually returning on page one.
   * @returns {Promise<object[]>} the accumulated items.
   */
  async getAllDatasets(params = {}, isDone = null) {
    const MAX_PAGES = 50;
    const pageSize = params.page_size || 100;
    const all = [];

    for (let page = 1; page <= MAX_PAGES; page += 1) {
      const response = await this.getDatasets({ ...params, page, page_size: pageSize });
      const items = response?.items || response?.datasets || [];
      all.push(...items);

      if (isDone && isDone(all)) return all;

      const totalPages = response?.total_pages
        ?? (response?.total ? Math.ceil(response.total / pageSize) : null);
      // Without either count, a short page is the only end-of-list signal.
      const finished = totalPages != null ? page >= totalPages : items.length < pageSize;
      if (finished) return all;
    }

    console.warn(`[api] getAllDatasets stopped at ${MAX_PAGES} pages; the list may be truncated`);
    return all;
  },

  /**
   * Get the latest completed extraction result for a dataset
   * @param {string} datasetId - Dataset ID
   * @returns {Promise<Object>} ExtractionJobResponse
   */
  // PRD-1336 R2: column names of a structured/interactive dataset's analytics
  // table, for building dataset-specific example prompts in the Insights wizard.
  // Best-effort — returns { columns: [] } for datasets with no materialized table.
  async getDatasetColumns(datasetId) {
    const url = `${API_BASE_URL}/api/v1/datasets/${datasetId}/columns`;
    const response = await fetch(url, { headers: { ...getAuthHeaders() } });
    if (!response.ok) throw new Error('Failed to get dataset columns');
    return response.json();
  },

  async getLatestExtractionResult(datasetId) {
    const url = `${API_BASE_URL}/api/v1/datasets/${datasetId}/extraction-results/latest`;
    const headers = { ...getAuthHeaders() };

    logRequest('GET', url, headers, null);

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers,
      });

      let responseData;
      const contentType = response.headers.get('content-type');

      if (contentType && contentType.includes('application/json')) {
        responseData = await response.json();
        logResponse(response, responseData);
      } else {
        const text = await response.text();
        responseData = { detail: text || 'Unexpected response format' };
      }

      if (!response.ok) {
        buildAndThrowApiError(response, responseData, 'Failed to get latest extraction result', 'getLatestExtractionResult');
      }

      return responseData;
    } catch (error) {
      throwIfNetworkError(error, 'getLatestExtractionResult');
      logError(error, 'getLatestExtractionResult - General Error');
      throw error;
    }
  },

  /**
   * Get all extraction jobs for a dataset, each with its own file breakdown.
   *
   * PRD-1387: this used to be described as a "per-pipeline" breakdown, which was
   * v1 vocabulary — one job per named graph. On v2 jobs are not partitioned that
   * way and the `pipeline` column is being retired (PRD-1386), so callers must
   * not key off it. A job parked for human input is identified by
   * `status === 'awaiting_input'`, and `csv_job_id` is the id the review
   * endpoints take. See utils/csvReviewJob.js.
   *
   * @param {string} datasetId - Dataset ID
   * @returns {Promise<Array>} Array of job objects: { id, status, csv_job_id?, files? }
   */
  async getDatasetJobs(datasetId) {
    const url = `${API_BASE_URL}/api/v1/datasets/${datasetId}/jobs`;
    const headers = { ...getAuthHeaders() };
    try {
      const response = await fetch(url, { method: 'GET', headers });
      const responseData = await response.json();
      if (!response.ok) {
        buildAndThrowApiError(response, responseData, 'Failed to get dataset jobs', 'getDatasetJobs');
      }
      return responseData;
    } catch (error) {
      throwIfNetworkError(error, 'getDatasetJobs');
      logError(error, 'getDatasetJobs - General Error');
      throw error;
    }
  },

  /**
   * Create a new connection
   * @param {Object} data - Connection data
   * @returns {Promise<Object>} Connection response
   */
  async createConnection(data) {
    const url = `${API_BASE_URL}/api/v1/connections`;
    const headers = {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    };

    logRequest('POST', url, headers, data);

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
      });

      let responseData;
      const contentType = response.headers.get('content-type');

      if (contentType && contentType.includes('application/json')) {
        responseData = await response.json();
        logResponse(response, responseData);
      } else {
        const text = await response.text();
        responseData = { detail: text || 'Unexpected response format' };
      }

      if (!response.ok) {
        buildAndThrowApiError(response, responseData, 'Failed to create connection', 'createConnection');
      }

      return responseData;
    } catch (error) {
      throwIfNetworkError(error, 'createConnection');
      logError(error, 'createConnection - General Error');
      throw error;
    }
  },

  async createSupportTicket(data, files = []) {
    const url = `${API_BASE_URL}/api/v1/support/tickets`;
    // Multipart: JSON payload as one field, files as repeated "attachments" fields.
    // Do NOT set Content-Type — the browser adds the multipart boundary.
    const headers = { ...getAuthHeaders() };
    const form = new FormData();
    form.append('payload', JSON.stringify(data));
    (files || []).forEach((f) => form.append('attachments', f, f.name));
    logRequest('POST', url, headers, { ...data, attachments: (files || []).map((f) => f.name) });
    try {
      const response = await fetch(url, { method: 'POST', headers, body: form });
      let responseData;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        responseData = await response.json();
      } else {
        const text = await response.text();
        responseData = { detail: text || 'Unexpected response format' };
      }
      logResponse(response, responseData);
      if (!response.ok) {
        buildAndThrowApiError(response, responseData, 'Failed to submit support request', 'createSupportTicket');
      }
      return responseData;
    } catch (error) {
      throwIfNetworkError(error, 'createSupportTicket');
      logError(error, 'createSupportTicket - General Error');
      throw error;
    }
  },

  /**
   * Get list of connections
   * @returns {Promise<Object>} Connections response
   */
  async getConnections() {
    const url = `${API_BASE_URL}/api/v1/connections`;
    const headers = { ...getAuthHeaders() };

    logRequest('GET', url, headers, null);

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers,
      });

      let responseData;
      const contentType = response.headers.get('content-type');

      if (contentType && contentType.includes('application/json')) {
        responseData = await response.json();
        logResponse(response, responseData);
      } else {
        const text = await response.text();
        responseData = { detail: text || 'Unexpected response format' };
      }

      if (!response.ok) {
        buildAndThrowApiError(response, responseData, 'Failed to get connections', 'getConnections');
      }

      return responseData;
    } catch (error) {
      throwIfNetworkError(error, 'getConnections');
      logError(error, 'getConnections - General Error');
      throw error;
    }
  },

  /**
   * PATCH /api/v1/connections/{id} — partial update of a connection.
   * @param {string} connectionId
   * @param {object} updates  e.g. { display_name, location, root_prefix, config, client_secret }
   */
  async updateConnection(connectionId, updates) {
    const url = `${API_BASE_URL}/api/v1/connections/${connectionId}`;
    const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
    logRequest('PATCH', url, headers, updates);
    try {
      const response = await fetch(url, { method: 'PATCH', headers, body: JSON.stringify(updates) });
      const responseData = response.headers.get('content-type')?.includes('application/json')
        ? await response.json()
        : { detail: (await response.text()) || 'Unexpected response format' };
      logResponse(response, responseData);
      if (!response.ok) buildAndThrowApiError(response, responseData, 'Failed to update connection', 'updateConnection');
      return responseData;
    } catch (error) {
      throwIfNetworkError(error, 'updateConnection');
      logError(error, 'updateConnection - General Error');
      throw error;
    }
  },

  /**
   * DELETE /api/v1/connections/{id}. Dependent automations unlink via FK
   * (ON DELETE SET NULL) — they keep their history but stop running until
   * pointed at a new connection.
   */
  async deleteConnection(connectionId) {
    const url = `${API_BASE_URL}/api/v1/connections/${connectionId}`;
    const headers = { ...getAuthHeaders() };
    logRequest('DELETE', url, headers, null);
    try {
      const response = await fetch(url, { method: 'DELETE', headers });
      logResponse(response, null);
      if (!response.ok && response.status !== 204) {
        const responseData = response.headers.get('content-type')?.includes('application/json')
          ? await response.json()
          : { detail: 'Failed to delete connection' };
        buildAndThrowApiError(response, responseData, 'Failed to delete connection', 'deleteConnection');
      }
      return true;
    } catch (error) {
      throwIfNetworkError(error, 'deleteConnection');
      logError(error, 'deleteConnection - General Error');
      throw error;
    }
  },

  /**
   * PATCH /api/v1/automations/{id} — partial update. Pause/resume flows
   * through `enabled` (the scheduler filters on it).
   */
  async updateAutomation(automationId, updates) {
    const url = `${API_BASE_URL}/api/v1/automations/${automationId}`;
    const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
    logRequest('PATCH', url, headers, updates);
    try {
      const response = await fetch(url, { method: 'PATCH', headers, body: JSON.stringify(updates) });
      const responseData = response.headers.get('content-type')?.includes('application/json')
        ? await response.json()
        : { detail: (await response.text()) || 'Unexpected response format' };
      logResponse(response, responseData);
      if (!response.ok) buildAndThrowApiError(response, responseData, 'Failed to update automation', 'updateAutomation');
      return responseData;
    } catch (error) {
      throwIfNetworkError(error, 'updateAutomation');
      logError(error, 'updateAutomation - General Error');
      throw error;
    }
  },

  /**
   * GET /api/v1/automations/{id} — one automation, with the fields the list omits.
   *
   * **The list and the single fetch do not return the same object.** The list
   * route builds its rows straight from the model; the single fetch runs them
   * through a builder that adds derived fields — `alert_summary` among them, the
   * sentence an edit conversation opens with. Opening an edit from a list row
   * therefore carries a null summary, and the opener renders nothing with no
   * error and no empty state, which makes the feature look absent rather than
   * broken. Raised in review of #195.
   *
   * Deliberately not fixed by pointing the list at the shared builder: that would
   * parse every stored spec on every list call to produce a sentence almost no
   * row will ever show. Whoever opens an edit already has the id.
   */
  async getAutomation(automationId) {
    // Encoded, like the alert activation route on the same path. An id
    // interpolated raw is a request whose TARGET the caller chooses: a value
    // containing `/` or `//host` walks out of the path and, with a protocol-
    // relative prefix, off the origin entirely — which is what CodeQL's
    // `js/client-side-request-forgery` flags, and it flagged this method.
    const url = `${API_BASE_URL}/api/v1/automations/${encodeURIComponent(automationId)}`;
    const headers = { ...getAuthHeaders() };
    logRequest('GET', url, headers, null);
    try {
      const response = await fetch(url, { method: 'GET', headers });
      logResponse(response, null);
      if (!response.ok) {
        const responseData = response.headers.get('content-type')?.includes('application/json')
          ? await response.json()
          : { detail: 'Failed to load automation' };
        buildAndThrowApiError(response, responseData, 'Failed to load automation', 'getAutomation');
      }
      return await response.json();
    } catch (error) {
      throwIfNetworkError(error, 'getAutomation');
      logError(error, 'getAutomation - General Error');
      throw error;
    }
  },

  /**
   * DELETE /api/v1/automations/{id}. RunHistory rows cascade via FK.
   */
  async deleteAutomation(automationId) {
    const url = `${API_BASE_URL}/api/v1/automations/${automationId}`;
    const headers = { ...getAuthHeaders() };
    logRequest('DELETE', url, headers, null);
    try {
      const response = await fetch(url, { method: 'DELETE', headers });
      logResponse(response, null);
      if (!response.ok && response.status !== 204) {
        const responseData = response.headers.get('content-type')?.includes('application/json')
          ? await response.json()
          : { detail: 'Failed to delete automation' };
        buildAndThrowApiError(response, responseData, 'Failed to delete automation', 'deleteAutomation');
      }
      return true;
    } catch (error) {
      throwIfNetworkError(error, 'deleteAutomation');
      logError(error, 'deleteAutomation - General Error');
      throw error;
    }
  },

  /**
   * Get current user profile (id, name, email) for automation notification display.
   * Uses GET /api/v1/user-profile; returns id, name, email (and other profile fields).
   * @returns {Promise<{ id: string, name: string, email?: string }>}
   */
  async getCurrentUser() {
    const url = `${API_BASE_URL}/api/v1/user-profile`;
    const headers = { ...getAuthHeaders() };

    try {
      const response = await fetch(url, { method: 'GET', headers });
      const contentType = response.headers.get('content-type');
      let responseData;
      if (contentType && contentType.includes('application/json')) {
        responseData = await response.json();
      } else {
        const text = await response.text();
        responseData = { detail: text || 'Unexpected response format' };
      }
      if (!response.ok) {
        buildAndThrowApiError(response, responseData, 'Failed to get current user', 'getCurrentUser');
      }
      return responseData;
    } catch (error) {
      throwIfNetworkError(error, 'getCurrentUser');
      logError(error, 'getCurrentUser - General Error');
      throw error;
    }
  },

  /**
   * Mark the current user's first-login onboarding overlay as complete (PRD-945).
   * Idempotent on the backend — calling twice does not double-stamp.
   * @returns {Promise<{ onboarding_completed_at: string }>}
   */
  async completeOnboarding() {
    const url = `${API_BASE_URL}/api/v1/user-profile/onboarding/complete`;
    const headers = { 'Content-Type': 'application/json', ...getAuthHeaders() };
    try {
      const response = await fetch(url, { method: 'POST', headers });
      const responseData = await response.json().catch(() => ({}));
      if (!response.ok) {
        buildAndThrowApiError(response, responseData, 'Failed to complete onboarding', 'completeOnboarding');
      }
      return responseData;
    } catch (error) {
      throwIfNetworkError(error, 'completeOnboarding');
      logError(error, 'completeOnboarding - General Error');
      throw error;
    }
  },

  /**
   * Update the current user's profile (PRD-09): display name, job title, department, photo URL.
   * PRD-09: combined atomic write for the merged Edit Profile modal. Sends the
   * profile + personalization blocks in ONE PATCH (one transaction, one audit row).
   * PATCH semantics — only provided fields change. Gated server-side on
   * manage_user_context_own; operates on the current user only (own-record).
   * @param {{
   *   profile?:{display_name?:string, job_title?:string, department?:string, avatar_type?:string, avatar_color?:string},
   *   personalization?:{about?:string, teams?:Array<{id?:string, name:string, members:Array<{name?:string, email?:string, user_id?:string}>}>, reviewers?:Array<{name?:string, email?:string, user_id?:string}>}
   * }} data
   * @returns {Promise<{ ok: boolean }>}
   */
  async updateProfile(data) {
    const url = `${API_BASE_URL}/api/v1/user-profile`;
    const headers = { 'Content-Type': 'application/json', ...getAuthHeaders() };
    try {
      const response = await fetch(url, { method: 'PATCH', headers, body: JSON.stringify(data) });
      const responseData = await response.json().catch(() => ({}));
      if (!response.ok) {
        buildAndThrowApiError(response, responseData, 'Failed to update profile', 'updateProfile');
      }
      return responseData;
    } catch (error) {
      throwIfNetworkError(error, 'updateProfile');
      logError(error, 'updateProfile - General Error');
      throw error;
    }
  },

  /**
   * Upload the current user's profile photo (PRD-09). Multipart; resapi validates type +
   * size, re-encodes to WebP, strips metadata, stores in Blob. Returns { ok, photo_url }.
   * @param {File} file
   */
  async uploadProfilePhoto(file) {
    const url = `${API_BASE_URL}/api/v1/user-profile/photo`;
    const headers = { ...getAuthHeaders() }; // no Content-Type — the browser sets the multipart boundary
    const form = new FormData();
    form.append('file', file);
    try {
      const response = await fetch(url, { method: 'POST', headers, body: form });
      const responseData = await response.json().catch(() => ({}));
      if (!response.ok) {
        buildAndThrowApiError(response, responseData, 'Failed to upload photo', 'uploadProfilePhoto');
      }
      return responseData;
    } catch (error) {
      throwIfNetworkError(error, 'uploadProfilePhoto');
      logError(error, 'uploadProfilePhoto - General Error');
      throw error;
    }
  },

  /**
   * Remove the current user's profile photo (PRD-09).
   */
  async deleteProfilePhoto() {
    const url = `${API_BASE_URL}/api/v1/user-profile/photo`;
    const headers = { ...getAuthHeaders() };
    try {
      const response = await fetch(url, { method: 'DELETE', headers });
      const responseData = await response.json().catch(() => ({}));
      if (!response.ok) {
        buildAndThrowApiError(response, responseData, 'Failed to remove photo', 'deleteProfilePhoto');
      }
      return responseData;
    } catch (error) {
      throwIfNetworkError(error, 'deleteProfilePhoto');
      logError(error, 'deleteProfilePhoto - General Error');
      throw error;
    }
  },

  /**
   * Fetch another asker's photo in a shared query (PRD-1645) as an object URL for <img src>.
   * Returns null when there's none, the path isn't the asker-photo route, or it fails. The
   * caller owns the object URL and must URL.revokeObjectURL it when done.
   */
  async fetchAskerPhotoObjectUrl(path) {
    // PRD-1645: `author_photo_url` on /turns is a resapi proxy path. Only that route is fetched
    // with the token, never an arbitrary URL.
    if (typeof path !== 'string' || !/^\/api\/v1\/threads\/[^/]+\/askers\/[^/]+\/photo$/.test(path)) return null;
    try {
      const response = await fetch(`${API_BASE_URL}${path}`, { method: 'GET', headers: { ...getAuthHeaders() } });
      if (!response.ok) return null;
      return URL.createObjectURL(await response.blob());
    } catch (error) {
      logError(error, 'fetchAskerPhotoObjectUrl - General Error');
      return null;
    }
  },

  /**
   * Fetch the current user's profile photo (server-proxied) as an object URL for <img src>.
   * The endpoint requires auth, so we fetch with the token and wrap the blob — an <img>
   * tag can't carry the auth header. Returns null when no photo is set (404). The caller
   * owns the object URL and must URL.revokeObjectURL it when done.
   */
  async fetchProfilePhotoObjectUrl() {
    const url = `${API_BASE_URL}/api/v1/user-profile/photo`;
    const headers = { ...getAuthHeaders() };
    try {
      // cache-bust so a freshly changed photo isn't served stale from the browser cache
      const response = await fetch(`${url}?t=${Date.now()}`, { method: 'GET', headers });
      if (!response.ok) return null;
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch (error) {
      logError(error, 'fetchProfilePhotoObjectUrl - General Error');
      return null;
    }
  },

  /**
   * Fetch a dataset file's raw bytes (server-proxied) as an object URL for <img src>
   * — used by the reviewer media-alongside-fields view (R5). The endpoint is auth'd
   * and dataset-read scoped, so we fetch with the token and wrap the blob (an <img>
   * can't carry the auth header). Returns null on any non-2xx (e.g. 404 no access).
   * The caller owns the object URL and must URL.revokeObjectURL it when done.
   */
  async fetchFileImageObjectUrl(datasetId, fileId) {
    const url = `${API_BASE_URL}/api/v1/datasets/${datasetId}/files/${fileId}/content`;
    const headers = { ...getAuthHeaders() };
    try {
      const response = await fetch(url, { method: 'GET', headers });
      if (!response.ok) return null;
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch (error) {
      logError(error, 'fetchFileImageObjectUrl - General Error');
      return null;
    }
  },

  /**
   * The time zones an organization can choose: `[{ name, utc_offset, featured }]`,
   * Canadian zones (featured) first. The org settings save validates against this list.
   */
  async getTimeZones() {
    const url = `${API_BASE_URL}/api/v1/time-zones`;
    const headers = { ...getAuthHeaders() };
    logRequest('GET', url, headers, null);
    try {
      const response = await fetch(url, { method: 'GET', headers });
      const responseData = await response.json();
      logResponse(response, responseData);
      if (!response.ok) {
        buildAndThrowApiError(response, responseData, 'Failed to load time zones', 'getTimeZones');
      }
      return Array.isArray(responseData?.zones) ? responseData.zones : [];
    } catch (error) {
      throwIfNetworkError(error, 'getTimeZones');
      logError(error, 'getTimeZones');
      throw error;
    }
  },

  /**
   * Upload the organization logo (PRD-09). OWNER-ONLY server-side. Multipart; resapi
   * validates type + size, re-encodes to WebP, strips metadata, stores in Blob.
   * @param {File} file
   */
  async uploadOrgLogo(file) {
    const url = `${API_BASE_URL}/api/v1/org/logo`;
    const headers = { ...getAuthHeaders() }; // no Content-Type — browser sets multipart boundary
    const form = new FormData();
    form.append('file', file);
    try {
      const response = await fetch(url, { method: 'POST', headers, body: form });
      const responseData = await response.json().catch(() => ({}));
      if (!response.ok) {
        buildAndThrowApiError(response, responseData, 'Failed to upload organization logo', 'uploadOrgLogo');
      }
      return responseData;
    } catch (error) {
      throwIfNetworkError(error, 'uploadOrgLogo');
      logError(error, 'uploadOrgLogo - General Error');
      throw error;
    }
  },

  /**
   * Remove the organization logo (PRD-09). OWNER-ONLY server-side.
   */
  async deleteOrgLogo() {
    const url = `${API_BASE_URL}/api/v1/org/logo`;
    const headers = { ...getAuthHeaders() };
    try {
      const response = await fetch(url, { method: 'DELETE', headers });
      const responseData = await response.json().catch(() => ({}));
      if (!response.ok) {
        buildAndThrowApiError(response, responseData, 'Failed to remove organization logo', 'deleteOrgLogo');
      }
      return responseData;
    } catch (error) {
      throwIfNetworkError(error, 'deleteOrgLogo');
      logError(error, 'deleteOrgLogo - General Error');
      throw error;
    }
  },

  /**
   * PRD-659/§6.7: the current user's own token usage for the Control / My Usage view.
   * Reads only own usage (gated manage_user_context_own server-side). Returns null when the
   * metering backend (Phase B) isn't available yet, so the view can show a preview instead.
   * @returns {Promise<object|null>}
   */
  async getMyUsage({ days } = {}) {
    const qs = days ? `?days=${encodeURIComponent(days)}` : '';
    const url = `${API_BASE_URL}/api/v1/usage/me${qs}`;
    const headers = { ...getAuthHeaders() };
    try {
      const response = await fetch(url, { method: 'GET', headers });
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      logError(error, 'getMyUsage - General Error');
      return null;
    }
  },

  /**
   * The activity categories actually present in the caller's org, for the filter
   * dropdown. Sourced from the data so the options can't drift from the rows.
   * @returns {Promise<string[]>}
   */
  async getAuditLogCategories() {
    const url = `${API_BASE_URL}/api/v1/audit-logs/categories`;
    const headers = { ...getAuthHeaders() };
    try {
      const response = await fetch(url, { method: 'GET', headers });
      if (!response.ok) return [];
      const data = await response.json();
      return Array.isArray(data?.categories) ? data.categories : [];
    } catch (error) {
      logError(error, 'getAuditLogCategories - General Error');
      return [];
    }
  },

  /**
   * Org-wide usage. Differs from getMyUsage in two ways that matter for accounting:
   * it has no user filter, so spend with no owning user is included, and it reports
   * non-token units (OCR pages, audio seconds) separately from the token total.
   * @returns {Promise<object|null>}
   */
  async getOrgUsage({ days } = {}) {
    const qs = days ? `?days=${encodeURIComponent(days)}` : '';
    const url = `${API_BASE_URL}/api/v1/usage/org${qs}`;
    const headers = { ...getAuthHeaders() };
    try {
      const response = await fetch(url, { method: 'GET', headers });
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      logError(error, 'getOrgUsage - General Error');
      return null;
    }
  },

  /**
   * Fetch the current user's org logo (server-proxied) as an object URL for <img src>.
   * Any authenticated user may view their own org's logo (to render it as their avatar).
   * Returns null when no logo is set (404). The caller owns the object URL and must
   * URL.revokeObjectURL it when done.
   */
  async fetchOrgLogoObjectUrl() {
    const url = `${API_BASE_URL}/api/v1/org/logo`;
    const headers = { ...getAuthHeaders() };
    try {
      const response = await fetch(`${url}?t=${Date.now()}`, { method: 'GET', headers });
      if (!response.ok) return null;
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch (error) {
      logError(error, 'fetchOrgLogoObjectUrl - General Error');
      return null;
    }
  },

  /**
   * Create a new automation
   * @param {Object} data - Automation data
   * @returns {Promise<Object>} Automation response
   */
  async createAutomation(data) {
    const url = `${API_BASE_URL}/api/v1/automations`;
    const headers = {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    };

    logRequest('POST', url, headers, data);

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
      });

      let responseData;
      const contentType = response.headers.get('content-type');

      if (contentType && contentType.includes('application/json')) {
        responseData = await response.json();
        logResponse(response, responseData);
      } else {
        const text = await response.text();
        responseData = { detail: text || 'Unexpected response format' };
      }

      if (!response.ok) {
        buildAndThrowApiError(response, responseData, 'Failed to create automation', 'createAutomation');
      }

      return responseData;
    } catch (error) {
      throwIfNetworkError(error, 'createAutomation');
      logError(error, 'createAutomation - General Error');
      throw error;
    }
  },

  /**
   * Run report query for preview (e.g. Regenerate in report automation wizard).
   * @param {Object} params - { queryText: string, datasetIds?: string[] }
   * @returns {Promise<{ response: string }>}
   */
  async runReportPreview(params) {
    const url = `${API_BASE_URL}/api/v1/automations/reports/preview`;
    const headers = { 'Content-Type': 'application/json', ...getAuthHeaders() };
    const body = {
      queryText: params.queryText || '',
      datasetIds: params.datasetIds || undefined,
    };
    try {
      const response = await fetch(url, { method: 'POST', headers, body: JSON.stringify(body) });
      const contentType = response.headers.get('content-type');
      let data;
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        const text = await response.text();
        data = { detail: text || 'Unexpected response' };
      }
      if (!response.ok) {
        buildAndThrowApiError(response, data, 'Report preview failed', 'runReportPreview');
      }
      return data;
    } catch (error) {
      throwIfNetworkError(error, 'runReportPreview');
      logError(error, 'runReportPreview');
      throw error;
    }
  },

  // ─────────────────────────────────────────────────────────────────────────
  // Thread / Chat API Methods
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Get the output formats available for a dataset selection — the union of
   * formats the selected documents' pathways support. Drives the chat
   * output-format picker.
   *
   * The server gates this by feature flag: when disabled it returns an empty
   * `formats` list, so the picker collapses to "None" (today's plain chat).
   *
   * @param {string[]} datasetIds - Currently selected dataset ids
   * @returns {Promise<{formats: string[], default: (string|null)}>}
   */
  async getOutputFormats(datasetIds = []) {
    // No selection ⇒ nothing to fill; skip the round-trip.
    if (!datasetIds || datasetIds.length === 0) {
      return { formats: [], default: null };
    }

    const url = new URL(`${API_BASE_URL}/api/v1/output-formats`);
    datasetIds.forEach((id) => url.searchParams.append('dataset_ids', id));

    const headers = { ...getAuthHeaders() };
    logRequest('GET', url.toString(), headers, null);

    try {
      const response = await fetch(url.toString(), { method: 'GET', headers });
      const data = await response.json();
      logResponse(response, data);
      if (!response.ok) {
        buildAndThrowApiError(response, data, 'Failed to get output formats', 'getOutputFormats');
      }
      return {
        formats: Array.isArray(data?.formats) ? data.formats : [],
        default: data?.default ?? null,
      };
    } catch (error) {
      throwIfNetworkError(error, 'getOutputFormats');
      logError(error, 'getOutputFormats - General Error');
      throw error;
    }
  },

  /**
   * Get list of chat threads for the current user
   * @param {Object} params - Query parameters (page, page_size)
   * @returns {Promise<Object>} ThreadListResponse with items and total
   */
  async getThreads(params = {}) {
    const { page = 1, page_size = 50, scope } = params;

    const url = new URL(`${API_BASE_URL}/api/v1/threads`);
    url.searchParams.append('page', page);
    url.searchParams.append('page_size', page_size);
    // PRD-1571: 'mine' | 'shared' | 'all'. Omitted ⇒ the server's default (own threads).
    if (scope) url.searchParams.append('scope', scope);

    logRequest('GET', url.toString(), {}, null);

    try {
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: { ...getAuthHeaders() },
      });

      const responseData = await response.json();
      logResponse(response, responseData);

      if (!response.ok) {
        throw new Error(responseData.detail || 'Failed to get threads');
      }

      return responseData;
    } catch (error) {
      logError(error, 'getThreads');
      throw error;
    }
  },

  /**
   * Create a new chat thread
   * @param {string} title - Optional title for the thread
   * @returns {Promise<Object>} ThreadResponse
   */
  async createThread(title = null) {
    const url = `${API_BASE_URL}/api/v1/threads`;
    const body = title ? { title } : {};

    logRequest('POST', url, { 'Content-Type': 'application/json', ...getAuthHeaders() }, body);

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(body),
      });

      const responseData = await response.json();
      logResponse(response, responseData);

      if (!response.ok) {
        throw new Error(responseData.detail || 'Failed to create thread');
      }

      return responseData;
    } catch (error) {
      logError(error, 'createThread');
      throw error;
    }
  },

  /**
   * Get a single thread by ID (includes export_* when set).
   * @param {string} threadId - Thread ID
   * @returns {Promise<Object>} ThreadResponse
   */
  async getThread(threadId) {
    const url = `${API_BASE_URL}/api/v1/threads/${threadId}`;
    const res = await fetch(url, { method: 'GET', headers: { ...getAuthHeaders() } });
    // A locked shared thread answers 403 with no body.
    const data = await res.json().catch(() => null);
    if (!res.ok) {
      // The status tells a shared query that stopped being shared (403) from one deleted (404).
      // A coded refusal's detail is `{ code, message }` (PRD-1646).
      const detail = data?.detail;
      const error = new Error((typeof detail === 'string' ? detail : detail?.message) || 'Failed to get thread');
      error.status = res.status;
      error.responseData = data;
      throw error;
    }
    return data;
  },

  /**
   * Rename a thread (PRD-1075). A user-set title persists and is not overwritten
   * by the auto-generated title.
   * @param {string} threadId - Thread ID
   * @param {string} title - New title
   * @returns {Promise<Object>} ThreadResponse
   */
  async updateThreadTitle(threadId, title) {
    const url = `${API_BASE_URL}/api/v1/threads/${threadId}`;
    const res = await fetch(url, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify({ title }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Failed to rename thread');
    return data;
  },

  /**
   * Set thread export (blob path and metadata) when chat receives export_result tool result.
   * @param {string} threadId - Thread ID
   * @param {Object} body - { blob_path, file_name?, format? } (format: 'pdf' | 'csv')
   * @returns {Promise<Object>} Updated ThreadResponse
   */
  async setThreadExport(threadId, body) {
    const url = `${API_BASE_URL}/api/v1/threads/${threadId}/export`;
    const res = await fetch(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Failed to set thread export');
    return data;
  },

  /**
   * Download thread export file (PDF/CSV). Uses auth headers. Returns blob or throws on 404.
   * @param {string} threadId - Thread ID
   * @returns {Promise<{ blob: Blob, filename: string }>}
   */
  async downloadThreadExport(threadId) {
    const url = `${API_BASE_URL}/api/v1/threads/${threadId}/export/download`;
    const res = await fetch(url, { method: 'GET', headers: { ...getAuthHeaders() } });
    if (res.status === 404) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.detail || 'This export has expired. Ask the assistant to regenerate the report.');
    }
    if (!res.ok) throw new Error('Download failed');
    const blob = await res.blob();
    const disp = res.headers.get('Content-Disposition');
    const filename = disp ? (disp.match(/filename="?([^";]+)"?/)?.[1] || 'export') : 'export';
    return { blob, filename };
  },

  /**
   * Delete a chat thread
   * @param {string} threadId - Thread ID
   * @returns {Promise<void>}
   */
  async deleteThread(threadId) {
    const url = `${API_BASE_URL}/api/v1/threads/${threadId}`;

    logRequest('DELETE', url, {}, null);

    try {
      const response = await fetch(url, {
        method: 'DELETE',
        headers: { ...getAuthHeaders() },
      });

      if (!response.ok) {
        const responseData = await response.json();
        throw new Error(responseData.detail || 'Failed to delete thread');
      }
    } catch (error) {
      logError(error, 'deleteThread');
      throw error;
    }
  },

  // ── Shared queries (PRD-1571; backend under PRD-1567) ─────────────────────
  // Errors carry .status so callers can tell a 403 (not owner / flag off, or a
  // locked thread) from a failure worth retrying.

  /**
   * Share a thread with the organisation, or make it private again. Owner only.
   * @param {string} threadId - Thread ID
   * @param {'organization'|'private'} visibility
   * @returns {Promise<Object>} Updated ThreadResponse
   */
  async setThreadVisibility(threadId, visibility) {
    const url = `${API_BASE_URL}/api/v1/threads/${encodeURIComponent(threadId)}`;
    const res = await fetch(url, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify({ visibility }),
    });
    const data = await res.json().catch(() => null);
    if (!res.ok) buildAndThrowApiError(res, data, 'Failed to update sharing', 'setThreadVisibility');
    return data;
  },

  /**
   * Re-pin a shared thread's datasets (owner only; the server answers every turn of a shared
   * thread over them). 409 if the thread isn't shared.
   * @param {string} threadId - Thread ID
   * @param {string[]} datasetIds - The datasets new turns use
   * @returns {Promise<Object>} Updated ThreadResponse
   */
  async setThreadDatasets(threadId, datasetIds) {
    const url = `${API_BASE_URL}/api/v1/threads/${encodeURIComponent(threadId)}`;
    const res = await fetch(url, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify({ dataset_ids: datasetIds }),
    });
    const data = await res.json().catch(() => null);
    if (!res.ok) buildAndThrowApiError(res, data, 'Failed to change the datasets', 'setThreadDatasets');
    return data;
  },

  /**
   * Who asked each turn of a thread, in order. Polled while a shared thread is open.
   * @param {string} threadId - Thread ID
   * @returns {Promise<Array<{id: string, seq: number, author_id: string, author_name: string,
   *   created_at: string, covered: boolean, datasets: string[]}>>}
   */
  async getThreadTurns(threadId) {
    const url = `${API_BASE_URL}/api/v1/threads/${encodeURIComponent(threadId)}/turns`;
    const res = await fetch(url, { method: 'GET', headers: { ...getAuthHeaders() } });
    // A locked thread answers 403 with no body.
    const data = await res.json().catch(() => null);
    if (!res.ok) buildAndThrowApiError(res, data, 'Failed to get conversation turns', 'getThreadTurns');
    return Array.isArray(data) ? data : [];
  },

  // PRD-1615: a pin is the current user's alone; pinning twice is a no-op.
  pinThread(threadId) {
    return apiService._pushRequest('PUT', `/api/v1/threads/${encodeURIComponent(threadId)}/pin`, undefined, 'pinThread');
  },
  unpinThread(threadId) {
    return apiService._pushRequest('DELETE', `/api/v1/threads/${encodeURIComponent(threadId)}/pin`, undefined, 'unpinThread');
  },

  /**
   * PRD-1619: stop the answer being prepared. Only the person who asked may stop it.
   * @returns {Promise<{stopped: boolean, reason: ?string}>} reason is not_running,
   *   not_stoppable, agent_unreachable, or still_stopping (stopped, thread not yet free)
   */
  stopAnswer(threadId) {
    return apiService._pushRequest('POST', `/api/v1/threads/${encodeURIComponent(threadId)}/stop`, undefined, 'stopAnswer');
  },

  /**
   * Send a message with streaming response (SSE)
   * Uses LangGraph's native streaming format.
   *
   * @param {string} threadId - Thread ID
   * @param {string} content - Message content
   * @param {Object} options - { onMessage, onDone, onError, datasetIds, checkpointId }
   *   onError's error has `connectionLost: true` when the stream dropped after it
   *   started, so the server may still finish the turn.
   * @returns {Function} Abort function to cancel the stream
   */
  sendMessageStream(threadId, content, { onMessage, onDone, onError, datasetIds = [], checkpointId = null, outputFormat = null }) {
    const url = `${API_BASE_URL}/api/v1/threads/${threadId}/messages/stream`;
    const abortController = new AbortController();

    logDebug('[API Service] Starting SSE stream to:', url);

    const body = { content };
    if (datasetIds?.length > 0) {
      body.dataset_ids = datasetIds;
    }
    if (checkpointId) {
      body.checkpoint_id = checkpointId;
    }
    // Omitted for the "None (plain answer)" path — the backend then returns
    // today's prose-only shape.
    if (outputFormat) {
      body.output_format = outputFormat;
    }

    // Set once the response starts streaming. A failure after that is a dropped
    // connection: the turn reached the server, which may still finish it.
    let streaming = false;

    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(body),
      signal: abortController.signal,
    })
      .then(async (response) => {
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          // `status` and `code` so the chat can tell a refused send (a "still answering"
          // refusal, or 403/404 on a shared query, PRD-1571 R4) from any other failure.
          // A busy refusal's detail is `{ code, message }`; others are text.
          const detail = errorData.detail;
          const message = typeof detail === 'string' ? detail : detail?.message;
          throw Object.assign(new Error(message || `HTTP ${response.status}`), {
            status: response.status,
            responseData: errorData,
            ...(detail?.code ? { code: detail.code } : {}),
          });
        }
        streaming = true;

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        // An event name lasts until the blank line that ends its event, not
        // until the end of a network read. resapi relays `event:` and `data:` as
        // separate writes, so they often arrive in different reads; resetting
        // per read turned the agent's `event: error` into an ordinary message
        // that nothing handled (PRD-1531).
        let currentEvent = 'message';
        // Whether the server ended the turn with `done` or `error`.
        let settled = false;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (!line.trim()) {
              currentEvent = 'message';
            } else if (line.startsWith('event:')) {
              currentEvent = line.slice(6).trim();
            } else if (line.startsWith('data:')) {
              const data = line.slice(5).trim();
              if (data) {
                try {
                  const parsed = JSON.parse(data);
                  // Be robust: sometimes the final SSE chunk can be parsed without the preceding `event: done`
                  // (chunk boundary / buffering). In that case, the backend still includes `type: "done"`.
                  if (parsed && parsed.type === 'done') {
                    settled = true;
                    onDone?.(parsed);
                  } else if (currentEvent === 'done') {
                    settled = true;
                    onDone?.(parsed);
                  } else if (currentEvent === 'error') {
                    settled = true;
                    // `code` lets the chat tell a refusal it can act on ("thread_busy")
                    // from a failure; `elapsed_s` says how long a timed-out run went on.
                    onError?.(Object.assign(
                      new Error(parsed.error),
                      parsed.code ? { code: parsed.code } : {},
                      typeof parsed.elapsed_s === 'number' ? { elapsedS: parsed.elapsed_s } : {},
                    ));
                  } else {
                    onMessage?.(parsed, currentEvent);
                  }
                } catch (e) {
                  // Non-JSON data, pass as string
                  onMessage?.(data, currentEvent);
                }
              }
            }
          }
        }

        // Closed with neither: a proxy or the network ended it. Left unreported,
        // the chat showed "Thinking" until the page was reloaded.
        if (!settled) {
          throw new Error('The response stream closed before the server finished the turn');
        }
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          if (streaming) error.connectionLost = true;
          logGenuineError('[API Service] Stream error:', error);
          onError?.(error);
        }
      });

    return () => abortController.abort();
  },

  /**
   * Send a message with file attachments
   * @param {string} threadId - Thread ID
   * @param {string} content - Message content
   * @param {FileList|Array} files - Files to attach
   * @returns {Promise<Object>} MessageResponse
   */
  async sendMessageWithFiles(threadId, content, files, datasetIds = [], outputFormat = null) {
    const url = `${API_BASE_URL}/api/v1/threads/${threadId}/messages/with-files`;
    const formData = new FormData();

    formData.append('content', content || '');

    if (datasetIds && datasetIds.length > 0) {
      formData.append('dataset_ids', datasetIds.join(','));
    }

    // Omitted for the "None (plain answer)" path.
    if (outputFormat) {
      formData.append('output_format', outputFormat);
    }

    const fileArray = Array.from(files);
    fileArray.forEach((file) => {
      formData.append('files', file);
    });

    logRequest('POST', url, {}, `FormData with ${fileArray.length} file(s)`);

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { ...getAuthHeaders() },
        body: formData,
      });

      // A non-JSON error body must still reach buildAndThrowApiError, which carries the
      // status the chat needs to recognise a "still answering" refusal.
      const responseData = await response.json().catch(() => null);
      logResponse(response, responseData);

      if (!response.ok) {
        buildAndThrowApiError(response, responseData, 'Failed to send message with files', 'sendMessageWithFiles');
      }

      return responseData;
    } catch (error) {
      logError(error, 'sendMessageWithFiles');
      throw error;
    }
  },

  /**
   * Resume a thread after human-in-the-loop interrupt
   * @param {string} threadId - Thread ID
   * @param {string} action - User action: 'approve' or 'reject'
   * @param {string} feedback - Optional feedback
   * @returns {Promise<Object>} MessageResponse
   */
  async resumeThread(threadId, action, feedback = null) {
    const url = `${API_BASE_URL}/api/v1/threads/${threadId}/resume`;
    const body = { action, feedback };

    logRequest('POST', url, { 'Content-Type': 'application/json', ...getAuthHeaders() }, body);

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(body),
      });

      const responseData = await response.json();
      logResponse(response, responseData);

      if (!response.ok) {
        throw new Error(responseData.detail || 'Failed to resume thread');
      }

      return responseData;
    } catch (error) {
      logError(error, 'resumeThread');
      throw error;
    }
  },

  /**
   * Get thread state (current messages)
   * @param {string} threadId - Thread ID
   * @param {string} checkpointId - Optional checkpoint ID for time travel
   * @returns {Promise<Object>} Thread state
   */
  async getThreadState(threadId, checkpointId = null) {
    let url = `${API_BASE_URL}/api/v1/threads/${threadId}/state`;
    if (checkpointId) {
      url += `?checkpoint_id=${checkpointId}`;
    }

    logRequest('GET', url, { ...getAuthHeaders() }, null);

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: { ...getAuthHeaders() },
      });

      const responseData = await response.json();
      logResponse(response, responseData);

      if (!response.ok) {
        throw new Error(responseData.detail || 'Failed to get thread state');
      }

      return responseData;
    } catch (error) {
      logError(error, 'getThreadState');
      throw error;
    }
  },

  /**
   * Get thread history for time travel
   * @param {string} threadId - Thread ID
   * @returns {Promise<Array>} Array of checkpoints
   */
  async getThreadHistory(threadId) {
    const url = `${API_BASE_URL}/api/v1/threads/${threadId}/history`;

    logRequest('GET', url, { ...getAuthHeaders() }, null);

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: { ...getAuthHeaders() },
      });

      const responseData = await response.json();
      logResponse(response, responseData);

      if (!response.ok) {
        throw new Error(responseData.detail || 'Failed to get thread history');
      }

      return responseData;
    } catch (error) {
      logError(error, 'getThreadHistory');
      throw error;
    }
  },

  /**
   * Get list of automations (paginated)
   * @param {Object} params - Query parameters (type, page, page_size)
   * @returns {Promise<Object>} { items, page, page_size, total }
   */
  async getAutomations(params = {}) {
    const { type, page = 1, page_size = 20 } = params;
    const searchParams = new URLSearchParams();
    if (type) searchParams.set('type', type);
    searchParams.set('page', String(page));
    searchParams.set('page_size', String(page_size));
    const url = `${API_BASE_URL}/api/v1/automations?${searchParams.toString()}`;

    logRequest('GET', url, { ...getAuthHeaders() }, null);

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: { ...getAuthHeaders() },
      });

      let responseData;
      const contentType = response.headers.get('content-type');

      if (contentType && contentType.includes('application/json')) {
        responseData = await response.json();
        logResponse(response, responseData);
      } else {
        const text = await response.text();
        responseData = { detail: text || 'Unexpected response format' };
      }

      if (!response.ok) {
        buildAndThrowApiError(response, responseData, 'Failed to get automations', 'getAutomations');
      }

      return responseData;
    } catch (error) {
      throwIfNetworkError(error, 'getAutomations');
      logError(error, 'getAutomations - General Error');
      throw error;
    }
  },

  /**
   * Get job status by job ID
   * @param {string} jobId - Job ID
   * @returns {Promise<Object>} JobStatusResponse with status
   */
  async getJobStatus(jobId) {
    const url = `${API_BASE_URL}/api/v1/jobs/${jobId}/status`;
    const headers = {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    };

    logRequest('GET', url, headers, null);

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers,
      });

      let responseData;
      const contentType = response.headers.get('content-type');

      if (contentType && contentType.includes('application/json')) {
        responseData = await response.json();
        logResponse(response, responseData);
      } else {
        const text = await response.text();
        responseData = { detail: text || 'Unexpected response format' };
      }

      if (!response.ok) {
        buildAndThrowApiError(response, responseData, 'Failed to get job status', 'getJobStatus');
      }

      return responseData;
    } catch (error) {
      throwIfNetworkError(error, 'getJobStatus');
      logError(error, 'getJobStatus - General Error');
      throw error;
    }
  },

  /**
   * Run an automation immediately
   * @param {string} automationId - Automation ID
   * @returns {Promise<Object>} Run response
   */
  async runAutomation(automationId) {
    const url = `${API_BASE_URL}/api/v1/automations/${automationId}/run`;
    const headers = {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    };

    logRequest('POST', url, headers, null);

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
      });

      let responseData;
      const contentType = response.headers.get('content-type');

      if (contentType && contentType.includes('application/json')) {
        responseData = await response.json();
        logResponse(response, responseData);
      } else {
        const text = await response.text();
        responseData = { detail: text || 'Unexpected response format' };
      }

      if (!response.ok) {
        buildAndThrowApiError(response, responseData, 'Failed to run automation', 'runAutomation');
      }

      return responseData;
    } catch (error) {
      throwIfNetworkError(error, 'runAutomation');
      logError(error, 'runAutomation - General Error');
      throw error;
    }
  },

  /**
   * POST /api/v1/automations/alerts/dry-run — evaluate an UNSAVED spec.
   *
   * This is what the wizard's live match count and per-channel message
   * previews are built on. It runs a real evaluation against the
   * structured-data service on every call, so callers must debounce.
   *
   * `timezone` is the schedule's zone. It decides when the alert runs, not what it
   * counts: the dry run, like a scheduled run, measures periods from today in the
   * organization's Primary Time Zone, and warns when the organization has none.
   *
   * @param {Object} params - { alertSpec, datasetIds, channels?, name?,
   *   timezone?, anchorDate?, volumeGuardMaxMatches?, volumeGuardMaxShare?,
   *   draftId?, automationId? }
   * @returns {Promise<Object>} AlertDryRunResponse
   */
  async dryRunUnsavedAlert(params, { signal, timeoutMs = 45000 } = {}) {
    const url = `${API_BASE_URL}/api/v1/automations/alerts/dry-run`;
    const headers = { 'Content-Type': 'application/json', ...getAuthHeaders() };
    const body = {
      alertSpec: params.alertSpec,
      datasetIds: params.datasetIds || [],
    };
    // Omit rather than send null — the endpoint treats absent and null
    // differently for the guard fields (absent means "no guard", and a null
    // share would fail the gt:0 bound).
    if (params.channels) body.channels = params.channels;
    if (params.name) body.name = params.name;
    if (params.timezone) body.timezone = params.timezone;
    if (params.anchorDate) body.anchorDate = params.anchorDate;
    if (params.volumeGuardMaxMatches != null) body.volumeGuardMaxMatches = params.volumeGuardMaxMatches;
    if (params.volumeGuardMaxShare != null) body.volumeGuardMaxShare = params.volumeGuardMaxShare;
    // **The query, by reference** (PRD-1438). The spec no longer carries `sql`,
    // because App Gateway's WAF answers a request body containing SQL with a 403
    // and no CORS headers. `buildDryRunParams` produced this reference, and this
    // allow-list dropped it before sending — so every dry run reached resapi with
    // neither the query nor a way to find it, on the form path as well as the
    // conversation one, and was refused as an unknown version-1 spec. The
    // builder's tests passed because they test the builder, not this method.
    if (params.draftId) body.draftId = params.draftId;
    // Edit mode: lets resapi fall back to the stored alert's query for a spec
    // the author did not rebuild.
    if (params.automationId) body.automationId = params.automationId;

    logRequest('POST', url, headers, body);

    // A real evaluation with no ceiling leaves the wizard spinning forever when
    // the structured-data service does not answer. The caller's signal and this
    // timeout are combined so either can end the request.
    const timeoutController = new AbortController();
    const timer = setTimeout(() => timeoutController.abort(), timeoutMs);
    const onCallerAbort = () => timeoutController.abort();
    if (signal) {
      if (signal.aborted) timeoutController.abort();
      else signal.addEventListener('abort', onCallerAbort, { once: true });
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
        signal: timeoutController.signal,
      });
      const contentType = response.headers.get('content-type');
      const data = contentType && contentType.includes('application/json')
        ? await response.json()
        : { detail: (await response.text()) || 'Unexpected response format' };
      logResponse(response, data);
      if (!response.ok) {
        buildAndThrowApiError(response, data, 'Dry run failed', 'dryRunUnsavedAlert');
      }
      return data;
    } catch (error) {
      // An abort the caller asked for is not a failure to report. One the timer
      // caused is, but it is a timeout rather than a network error, and the two
      // read very differently to whoever is waiting.
      if (error?.name === 'AbortError') {
        if (signal?.aborted) throw error;
        const timeout = new Error(`Dry run timed out after ${Math.round(timeoutMs / 1000)}s`);
        timeout.isTimeout = true;
        logError(timeout, 'dryRunUnsavedAlert - Timeout');
        throw timeout;
      }
      throwIfNetworkError(error, 'dryRunUnsavedAlert');
      logError(error, 'dryRunUnsavedAlert - General Error');
      throw error;
    } finally {
      clearTimeout(timer);
      if (signal) signal.removeEventListener('abort', onCallerAbort);
    }
  },

  /**
   * POST /api/v1/automations/alerts/ir/author — a question, turned into a query.
   *
   * The authoring half of the alert wizard (PRD-1355). Sends the author's
   * sentence and the datasets it may read; gets back the query to store (`sql`),
   * the window its placeholders bind to (`window`), the columns its result
   * carries, a capped sample of rows and the total match count.
   *
   * Version 3 (PRD-1405). `ir` still travels in the response while the far side
   * keeps returning it, and nothing here reads it any more.
   *
   * **`allowedTables` is deliberately not a parameter.** The field exists on the
   * far side's request model and resapi supplies it from the caller's own
   * permissions; the route refuses a body carrying one outright, because the
   * console choosing its own read scope is the security boundary being
   * confused. Nothing here should ever be tempted to add it.
   *
   * **A refusal is a 4xx carrying `detail.reason`, and the class is the
   * contract.** The wizard branches on it to decide what to render. The error
   * thrown below therefore carries `reason` through rather than flattening it
   * into prose, or the caller would be left matching on message text.
   *
   * @param {Object} params - { question, datasetIds, previewRows?, anchorDate? }
   * @returns {Promise<Object>} { sql, window, columns, rows, matchCount }
   */
  // **Longer than the dry run's, deliberately, and longer than resapi's.**
  // 45s was copied from the dry run, which is a single SQL execution. Authoring
  // runs the generation loop — measured at 69-92s per sentence on a real
  // dataset — and resapi cut at 60s. At 45s the console gave up first, so every
  // real attempt surfaced as a generic transport error and the classed refusal
  // that arrived later was thrown away.
  //
  // This budget only has to be the *last* to expire, so whatever resapi decides
  // is what the author is told.
  //
  // **Raised from 120s with resapi's own bound, because that ordering is the
  // whole point.** resapi now waits 180s (`_AUTHOR_TIMEOUT_SECONDS`, PRD-1401),
  // sized above the far side's 120s generation budget plus its preview. Left at
  // 120s the console would once again be the first to give up, and a classed
  // refusal arriving at 140s would be thrown away for a generic transport error
  // — exactly the failure the paragraph above describes, reintroduced by moving
  // the other number rather than by touching this one.
  //
  // Safe if PRD-1401 does not land: against a 60s resapi the console simply
  // never expires first, which is the intended ordering either way. Found in
  // review of #177, against a live stack; re-broken and re-fixed in PRD-1405.
  async authorAlertIR(params, { signal, timeoutMs = 200000 } = {}) {
    const url = `${API_BASE_URL}/api/v1/automations/alerts/ir/author`;
    const headers = { 'Content-Type': 'application/json', ...getAuthHeaders() };
    const body = {
      question: params.question,
      datasetIds: params.datasetIds || [],
    };
    if (params.previewRows != null) body.previewRows = params.previewRows;
    if (params.anchorDate) body.anchorDate = params.anchorDate;

    logRequest('POST', url, headers, body);

    // Same ceiling as the dry run, and for the same reason: this reaches the
    // structured-data service and a wizard with no timeout spins forever.
    const timeoutController = new AbortController();
    const timer = setTimeout(() => timeoutController.abort(), timeoutMs);
    const onCallerAbort = () => timeoutController.abort();
    if (signal) {
      if (signal.aborted) timeoutController.abort();
      else signal.addEventListener('abort', onCallerAbort, { once: true });
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
        signal: timeoutController.signal,
      });
      const contentType = response.headers.get('content-type');
      const data = contentType && contentType.includes('application/json')
        ? await response.json()
        : { detail: (await response.text()) || 'Unexpected response format' };
      logResponse(response, data);
      if (!response.ok) {
        // The classed refusal, kept as a class. Thrown rather than returned so
        // the call site's error path handles it, but with `reason` attached so
        // that path can tell "this question needs a threshold" from "the
        // service is down".
        const reason = data?.detail?.reason;
        if (reason) {
          const refusal = new Error(data?.detail?.message || 'That question could not be turned into an alert.');
          refusal.reason = reason;
          // The specific construct the refusal is about — a column that does
          // not exist, a date the generator pinned. Carried because the class
          // alone is often too coarse to write a useful sentence from, and
          // because matching on the message text to recover it is the
          // brittleness the classification exists to remove.
          refusal.construct = data?.detail?.construct;
          refusal.status = response.status;
          logError(refusal, `authorAlertIR - refused (${reason})`);
          throw refusal;
        }
        buildAndThrowApiError(response, data, 'Could not build the alert query', 'authorAlertIR');
      }
      return data;
    } catch (error) {
      if (error?.name === 'AbortError') {
        if (signal?.aborted) throw error;
        const timeout = new Error(`Building the alert query timed out after ${Math.round(timeoutMs / 1000)}s`);
        timeout.isTimeout = true;
        logError(timeout, 'authorAlertIR - Timeout');
        throw timeout;
      }
      // A classed refusal is not a network failure and must not be re-wrapped.
      if (error?.reason) throw error;
      throwIfNetworkError(error, 'authorAlertIR');
      logError(error, 'authorAlertIR - General Error');
      throw error;
    } finally {
      clearTimeout(timer);
      if (signal) signal.removeEventListener('abort', onCallerAbort);
    }
  },

  /**
   * POST /api/v1/automations/alerts/conversation — one turn of conversational
   * authoring (PRD-1381).
   *
   * **Stateless: the whole transcript and the slots go up every turn.** Neither
   * resapi nor the far side keeps them — the agent's graph compiles with no
   * checkpointer — so the console owns the conversation. Losing this state loses
   * the conversation, which is why every reply hands `slots` back.
   *
   * `agentUnavailable` is set on the thrown error when the failure is the agent
   * rather than the author: a timeout, a transport error, a 502/503. The caller
   * falls back to the form on that, because replacing the wizard made authoring
   * depend on a model being reachable, and the form always worked.
   *
   * **This budget only has to be the LAST to expire**, so whatever resapi
   * decides is what the author is told. That reasoning is unchanged from review
   * of #177; the number is not.
   *
   * It was 120s while resapi waited 60s and the agent was allowed two Build
   * attempts of 180s each. The ordering here was right — resapi expired first,
   * as intended — but it expired *inside* the work it proxies, so an ordinary
   * slow Build came back as a 502, which the caller latches as an outage and
   * answers by discarding the conversation. Fixing that raised resapi to 300s,
   * which would have made this the first to expire and inverted the chain the
   * other way.
   *
   * The chain now reads: agent 2 x 120s, resapi 300s, this 330s. A Build is a
   * 29s median and an 86s maximum, so none of them should ever be reached — the
   * order only matters when one is.
   */
  async alertConversationTurn(params, { signal, timeoutMs = 330000 } = {}) {
    const url = `${API_BASE_URL}/api/v1/automations/alerts/conversation`;
    const headers = { 'Content-Type': 'application/json', ...getAuthHeaders() };
    const body = {
      // **Only `role` and `content` go on the wire, and this is load-bearing.**
      //
      // resapi's `AlertConversationTurn` sets `extra: "forbid"`, and the thread
      // carries `options` on each assistant message so it can render them beside
      // the question they belong to. The first turn sends one user message and
      // validates; **every follow-up replays an assistant message and is refused
      // with a 422** whose `detail` is FastAPI's list of validation errors — not
      // a string, so the handler below falls through to "The alert assistant
      // could not answer that." and the author is told the agent failed.
      //
      // The two halves landed on the same day in different repos: the strict
      // model in micro-services-stack (PRD-1380) and `options` on the message
      // here (PRD-1381). Neither is wrong on its own.
      //
      // Stripping here rather than loosening the model: `extra: "forbid"` is
      // deliberate — the transcript becomes a prompt paid for per turn, and the
      // one field a confused console would send is the read scope. The agent has
      // no use for `options`; it is this component's rendering state.
      // `m?.role` rather than destructuring: a malformed entry would otherwise
      // throw here, turning a request the far side would have refused into a
      // client-side exception. This never introduces a failure the old code
      // did not have.
      messages: (params.messages || []).map((m) => ({ role: m?.role, content: m?.content })),
      slots: params.slots || {},
      datasetIds: params.datasetIds || [],
    };
    if (params.anchorDate) body.anchorDate = params.anchorDate;

    logRequest('POST', url, headers, body);

    const timeoutController = new AbortController();
    const timer = setTimeout(() => timeoutController.abort(), timeoutMs);
    const onCallerAbort = () => timeoutController.abort();
    if (signal) {
      if (signal.aborted) timeoutController.abort();
      else signal.addEventListener('abort', onCallerAbort, { once: true });
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
        signal: timeoutController.signal,
      });
      const contentType = response.headers.get('content-type');
      const data = contentType && contentType.includes('application/json')
        ? await response.json()
        : { detail: (await response.text()) || 'Unexpected response format' };
      logResponse(response, data);

      if (!response.ok) {
        // 429 is the far side's concurrency bound, not an outage: somebody else
        // is building. Retrying works, so it must NOT trip the fallback —
        // dropping an author into the form because the system was briefly busy
        // would be a worse answer than asking them to wait.
        const isAgentFailure = response.status === 502 || response.status === 503;
        // **"The agent could not answer" has to be true when we say it.**
        //
        // `detail` arrives in three shapes and only one of them was read. A
        // FastAPI request-validation failure puts a LIST of `{loc, msg, type}`
        // there, and a classed refusal puts an object carrying `message` — both
        // are not strings, so both fell to the sentence below and were reported
        // as the agent failing.
        //
        // That is not a cosmetic difference. It is what disguised the six-day
        // follow-up bug this branch fixes: every turn was rejected before it
        // reached the agent, the console said the agent failed, and the far side
        // was suspected for days. Left alone it would disguise the next one, and
        // the next one will not have an obviously broken feature pointing at it.
        const detail = data?.detail;
        const rejectedByValidation = Array.isArray(detail);
        let failureMessage;
        if (typeof detail === 'string' && detail) {
          failureMessage = detail;
        } else if (detail && typeof detail === 'object' && !Array.isArray(detail)
          && typeof detail.message === 'string' && detail.message) {
          // A classed refusal: `{reason, construct, message}`. The message is
          // written for the author and is the whole point of classing it.
          failureMessage = detail.message;
        } else if (rejectedByValidation) {
          // The agent was never asked, so it cannot have failed. The author
          // cannot act on this — it is a defect on our side — so it says what to
          // do rather than describing a validation error they did not cause. The
          // `loc` that identifies it is already in the console via logResponse.
          failureMessage = 'That message could not be sent. Please try again, and reload the page if it keeps happening.';
        } else {
          failureMessage = 'The alert assistant could not answer that.';
        }
        const failure = new Error(failureMessage);
        failure.status = response.status;
        failure.agentUnavailable = isAgentFailure;
        // Set so a caller can tell a request this console built wrongly from an
        // agent that genuinely could not answer. Nothing branches on it yet;
        // it exists so the next reader does not have to re-derive it from the
        // message text, which is how this class of bug hides.
        failure.requestRejected = rejectedByValidation;
        failure.retryAfter =
          response.status === 429 ? Number(response.headers.get('Retry-After')) || 30 : null;
        logError(failure, `alertConversationTurn - ${response.status}`);
        throw failure;
      }
      return data;
    } catch (error) {
      if (error?.name === 'AbortError') {
        if (signal?.aborted) throw error;
        const timeout = new Error(
          `The alert assistant did not answer within ${Math.round(timeoutMs / 1000)}s`,
        );
        timeout.isTimeout = true;
        // A timeout is the agent being unreachable from the author's point of
        // view, whatever the cause. They should get the form, not a spinner.
        timeout.agentUnavailable = true;
        logError(timeout, 'alertConversationTurn - Timeout');
        throw timeout;
      }
      if (error?.status) throw error;
      // No status means no response at all — a network failure.
      error.agentUnavailable = true;
      throwIfNetworkError(error, 'alertConversationTurn');
      logError(error, 'alertConversationTurn - General Error');
      throw error;
    } finally {
      clearTimeout(timer);
      if (signal) signal.removeEventListener('abort', onCallerAbort);
    }
  },

  /**
   * POST /api/v1/automations/{id}/alert/activate|deactivate.
   *
   * Activation is a separate action from create and edit on purpose — it gets
   * its own audit entry, which is why neither `AutomationCreate` nor
   * `AutomationUpdate` carries an active flag.
   *
   * Activating a broken alert is refused; deactivating never is. Requiring a
   * valid spec to *stop* an alert would be backwards.
   *
   * @param {string} automationId
   * @param {boolean} active - true to activate, false to deactivate
   * @param {string} [note] - recorded on the audit entry
   */
  async setAlertActive(automationId, active, note) {
    const verb = active ? 'activate' : 'deactivate';
    // Encoded to satisfy CodeQL's client-side-request-forgery rule. The id comes
    // from an API response rather than user input, so this is defensive rather
    // than a live hole — but the encoding costs nothing and the alert is noise
    // otherwise.
    const url = `${API_BASE_URL}/api/v1/automations/${encodeURIComponent(automationId)}/alert/${verb}`;
    const headers = { 'Content-Type': 'application/json', ...getAuthHeaders() };
    const body = note ? { note } : {};
    logRequest('POST', url, headers, body);
    try {
      const response = await fetch(url, { method: 'POST', headers, body: JSON.stringify(body) });
      const contentType = response.headers.get('content-type');
      const data = contentType && contentType.includes('application/json')
        ? await response.json()
        : { detail: (await response.text()) || 'Unexpected response format' };
      logResponse(response, data);
      if (!response.ok) {
        buildAndThrowApiError(response, data, `Failed to ${verb} alert`, 'setAlertActive');
      }
      return data;
    } catch (error) {
      throwIfNetworkError(error, 'setAlertActive');
      logError(error, 'setAlertActive - General Error');
      throw error;
    }
  },

  async getAuditLogs(params = {}) {
    const { page = 1, page_size = 10, category, action, q, mine, days } = params;
    const url = new URL(`${API_BASE_URL}/api/v1/audit-logs`);
    url.searchParams.append('page', page);
    url.searchParams.append('page_size', page_size);
    if (category) url.searchParams.append('category', category);
    if (action) url.searchParams.append('action', action);
    if (q) url.searchParams.append('q', q);
    if (mine) url.searchParams.append('mine', 'true');
    if (days) url.searchParams.append('days', days);

    const headers = { ...getAuthHeaders() };
    logRequest('GET', url.toString(), headers, null);

    try {
      const response = await fetch(url.toString(), { method: 'GET', headers });
      const responseData = await response.json();
      logResponse(response, responseData);

      if (!response.ok) {
        buildAndThrowApiError(response, responseData, 'Failed to fetch audit logs', 'getAuditLogs');
      }
      return responseData;
    } catch (error) {
      throwIfNetworkError(error, 'getAuditLogs');
      logError(error, 'getAuditLogs - General Error');
      throw error;
    }
  },

  async loginStart(email) {
    const url = `${API_BASE_URL}/login/start`;
    const body = { email: email.trim() };
    logRequest('POST', url, { 'Content-Type': 'application/json' }, body);
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        // Required so browser stores `sso_state` + `sso_intent` cookies set by the API.
        // Microsoft's redirect to /login/callback then sends them back for CSRF + intent verification.
        credentials: 'include',
      });
      const responseData = await response.json().catch(() => ({}));
      logResponse(response, responseData);
      if (!response.ok) {
        const err = new Error(responseData.detail || 'Login failed');
        err.status = response.status;
        err.responseData = responseData;
        throw err;
      }
      return responseData;
    } catch (error) {
      throwIfNetworkError(error, 'loginStart');
      throw error;
    }
  },

  async otpVerify(email, code) {
    const url = `${API_BASE_URL}/login/otp/verify`;
    const body = { email: email.trim(), code };
    logRequest('POST', url, { 'Content-Type': 'application/json' }, body);
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const responseData = await response.json().catch(() => ({}));
      logResponse(response, responseData);
      if (!response.ok) {
        const err = new Error(responseData.detail || 'Invalid or expired code');
        err.status = response.status;
        err.responseData = responseData;
        throw err;
      }
      return responseData;
    } catch (error) {
      throwIfNetworkError(error, 'otpVerify');
      throw error;
    }
  },

  async otpResend(email) {
    const url = `${API_BASE_URL}/login/otp/send`;
    const body = { email: email.trim() };
    logRequest('POST', url, { 'Content-Type': 'application/json' }, body);
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const responseData = await response.json().catch(() => ({}));
      logResponse(response, responseData);
      if (!response.ok) {
        const err = new Error(responseData.detail || 'Unable to resend code');
        err.status = response.status;
        err.responseData = responseData;
        throw err;
      }
      return responseData;
    } catch (error) {
      throwIfNetworkError(error, 'otpResend');
      throw error;
    }
  },

  async getWidgetTypes() {
    const url = `${API_BASE_URL}/api/v1/widgets/types`;
    const response = await fetch(url, { headers: { ...getAuthHeaders() } });
    const data = await response.json();
    if (!response.ok) throw new Error(data.detail || 'Failed to get widget types');
    return data;
  },

  async executeWidgetQuery(widgetTypeId, prompt, datasetIds = [], timeRange, opts = {}) {
    const url = `${API_BASE_URL}/api/v1/widgets/query`;
    const body = {
      widget_type_id: widgetTypeId,
      prompt,
      dataset_ids: datasetIds.length ? datasetIds : undefined,
      time_range: timeRange || undefined,
      // PRD-1370 #15: a saved chart's refresh re-runs its stored SQL deterministically.
      // Only the widget_id is sent — resapi loads the stored SQL and resolves the
      // permitted tables server-side, so the client never chooses the query or its read
      // scope (security review on #466). The field names below are a rendering hint only.
      widget_id: opts.widget_id || undefined,
      // Which tile of a summary row is asking. A strip is several independent
      // queries, so the widget id alone does not identify one; the index only
      // selects, and the query itself is read from the widget's own tile config.
      tile_index: opts.tile_index ?? undefined,
      label_field: opts.label_field || undefined,
      value_field: opts.value_field || undefined,
      series_field: opts.series_field || undefined,
      bin_width: opts.bin_width || undefined,
    };
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(body),
    });
    // A failing response is not always JSON. An ingress or proxy error returns an
    // HTML page, and parsing it throws "Unexpected token '<'" — which useWidgetQuery
    // catches and puts on the card as the failure message. Guard the parse so the
    // reader gets a sentence rather than a parser complaint.
    let data;
    try {
      data = await response.json();
    } catch {
      // Mirrors QUERY_FAILED in the backend's widget_messages catalogue, which owns
      // this copy. It cannot be served from there: there is no parseable response to
      // carry it. Keep the two in step.
      if (!response.ok) throw widgetQueryError('The widget query could not be completed. Try again.', response.status);
      throw widgetQueryError('The widget query returned a response that could not be read.', response.status);
    }
    if (!response.ok) throw widgetQueryError(data.detail || 'Widget query failed', response.status);
    return data;
  },

  // Counts about the Console itself, for the non-prompted tiles of a Summary row.
  // No agent involved, so these are cheap and deliberately do NOT take a slot in
  // useWidgetQuery's concurrency gate. Each metric resolves independently server
  // side: one that fails comes back as a reason beside the ones that worked, so a
  // single broken tile cannot empty the strip.
  async getPlatformMetrics(metrics = []) {
    const qs = encodeURIComponent(metrics.join(','));
    const url = `${API_BASE_URL}/api/v1/widgets/platform-metrics?metrics=${qs}`;
    const response = await fetch(url, { headers: { ...getAuthHeaders() } });
    const data = await response.json();
    if (!response.ok) {
      // Carry the status: a family whose flag is off answers 403 here, and a tile
      // has to tell that closed door apart from a fault so it can say "not enabled"
      // rather than "unavailable".
      const err = new Error(data.detail || 'Failed to load platform metrics');
      err.status = response.status;
      throw err;
    }
    return data;
  },

  async listWidgets() {
    const url = `${API_BASE_URL}/api/v1/widgets`;
    const response = await fetch(url, { headers: { ...getAuthHeaders() } });
    const data = await response.json();
    if (!response.ok) throw new Error(data.detail || 'Failed to list widgets');
    return data;
  },

  async saveWidget(widget) {
    const url = `${API_BASE_URL}/api/v1/widgets`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(widget),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.detail || 'Failed to save widget');
    return data;
  },

  async updateWidget(widgetId, widget) {
    const url = `${API_BASE_URL}/api/v1/widgets/${widgetId}`;
    const response = await fetch(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(widget),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.detail || 'Failed to update widget');
    return data;
  },

  // PRD-1336 R4 (PR #176 #4): narrow refresh write-back — persists only the fresh
  // result + compute time, so a background refresh can't overwrite a concurrent
  // config edit or reset canvas size. Owner-only server-side (viewers get a 404).
  async patchWidgetData(widgetId, data) {
    const url = `${API_BASE_URL}/api/v1/widgets/${widgetId}/data`;
    const response = await fetch(url, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify({ data }),
    });
    const body = await response.json();
    if (!response.ok) throw new Error(body.detail || 'Failed to update widget');
    return body;
  },

  // PRD-1578: change a widget's words -- `title`, or a KPI's `display_label` -- without
  // asking its question again. Runs no query and leaves the result and its compute time
  // alone. Send only the field being changed; a `display_label` of null clears the edit.
  async patchWidgetText(widgetId, fields) {
    const url = `${API_BASE_URL}/api/v1/widgets/${widgetId}`;
    const response = await fetch(url, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(fields),
    });
    // Parsed defensively, as openWidgetConversation does: an ingress 502 is HTML, and a
    // 401 has to go through buildAndThrowApiError to clear the session.
    let responseData;
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      try {
        responseData = await response.json();
      } catch {
        responseData = { detail: 'Invalid JSON response' };
      }
    } else {
      const text = await response.text();
      responseData = { detail: text || 'Unexpected response format' };
    }
    if (!response.ok) buildAndThrowApiError(response, responseData, 'Failed to update widget', 'patchWidgetText');
    return responseData;
  },

  async deleteWidget(widgetId) {
    const url = `${API_BASE_URL}/api/v1/widgets/${widgetId}`;
    const response = await fetch(url, {
      method: 'DELETE',
      headers: { ...getAuthHeaders() },
    });
    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || 'Failed to delete widget');
    }
    return { status: 'deleted' };
  },

  // --- Insights dashboard tabs ---
  // Every call throws an Error carrying `.status`, so the dashboard can tell a refusal
  // (409: name taken, last tab) from a failure. The body is parsed guarded: an ingress
  // error page is HTML, and parsing it first would lose the status.
  async dashboardTabRequest(path, method = 'GET', body) {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      method,
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    let data = null;
    try {
      data = await response.json();
    } catch {
      data = null;
    }
    if (!response.ok) {
      const err = new Error(data?.detail || 'The dashboard could not be updated. Try again.');
      err.status = response.status;
      throw err;
    }
    return data;
  },

  listDashboardTabs() {
    return this.dashboardTabRequest('/api/v1/dashboard-tabs');
  },

  createDashboardTab(name) {
    return this.dashboardTabRequest('/api/v1/dashboard-tabs', 'POST', { name });
  },

  // `changes` is { name } and/or { visibility: 'private' | 'organization' }.
  updateDashboardTab(tabId, changes) {
    return this.dashboardTabRequest(`/api/v1/dashboard-tabs/${tabId}`, 'PATCH', changes);
  },

  deleteDashboardTab(tabId) {
    return this.dashboardTabRequest(`/api/v1/dashboard-tabs/${tabId}`, 'DELETE');
  },

  reorderDashboardTabs(tabIds) {
    return this.dashboardTabRequest('/api/v1/dashboard-tabs/order', 'PUT', { tab_ids: tabIds });
  },

  setDashboardTabHidden(tabId, hidden) {
    return this.dashboardTabRequest(`/api/v1/dashboard-tabs/${tabId}/hidden`, 'PUT', { hidden });
  },

  // `layouts` is { [widgetId]: { lg: { x, y, w, h }, md: {...}, ... } }.
  saveDashboardTabLayout(tabId, layouts) {
    return this.dashboardTabRequest(`/api/v1/dashboard-tabs/${tabId}/layout`, 'PUT', { layouts });
  },

  shareAllDashboardTabs() {
    return this.dashboardTabRequest('/api/v1/dashboard-tabs/share-all', 'POST');
  },

  moveWidgetToTab(widgetId, tabId) {
    return this.dashboardTabRequest(`/api/v1/widgets/${widgetId}/tab`, 'PUT', { tab_id: tabId });
  },

  // PRD-1336 R6: authorize a client-side dashboard PDF export. The PDF itself is
  // built in the browser from the rendered widgets; this call is the server-side
  // gate — it 403s if the user can't read a dataset behind any widget on the page
  // (so a shared dashboard can't leak an unreadable dataset) and records the audit
  // entry. Must succeed BEFORE the PDF is generated.
  // Every row behind one table widget, for a CSV download. Distinct from
  // authorizeDashboardExport below, which authorizes a browser-side PDF of the whole
  // dashboard as displayed: this re-runs the widget's own stored query server-side,
  // so it returns the rows the card had to cap, not the ones it drew.
  async exportWidgetRows(widgetId) {
    const url = `${API_BASE_URL}/api/v1/widgets/${widgetId}/export`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    });
    // Parse AFTER the status, guarded — the same shape executeWidgetQuery uses. A
    // failing response is not always JSON: an ingress or proxy error returns an HTML
    // page, and parsing it first threw "Unexpected token '<'" with no `status`
    // attached, so handleDownloadCsv missed its 403/409/410 branches and put the raw
    // SyntaxError on screen as a toast.
    //
    // This endpoint is the likeliest in the console to meet one. A 50,000-row export
    // with an exact count is the slowest call any widget makes and the backend allows
    // it 120 seconds, which is longer than ingress timeouts commonly are — so the 504
    // page is a realistic outcome for exactly the large exports this feature exists
    // for. Raised in review of #205.
    let data;
    try {
      data = await response.json();
    } catch {
      if (!response.ok) throw widgetQueryError('The export could not be produced. Try again.', response.status);
      throw widgetQueryError('The export returned a response that could not be read.', response.status);
    }
    if (!response.ok) {
      const err = widgetQueryError(data.detail || 'Export failed', response.status);
      err.responseData = data;
      throw err;
    }
    return data;
  },

  async authorizeDashboardExport(widgetIds) {
    const url = `${API_BASE_URL}/api/v1/widgets/export`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify({ widget_ids: widgetIds }),
    });
    const data = await response.json();
    if (!response.ok) {
      const err = new Error(data.detail || 'Export not permitted');
      err.status = response.status;
      err.responseData = data;
      throw err;
    }
    return data;
  },

  // PRD-1337: open (or reuse) this viewer's drill-down conversation for a widget.
  // Returns { thread_id, frozen_context, created }. 403 if the viewer lacks access
  // to the widget's dataset (no conversation opens).
  async openWidgetConversation(widgetId) {
    const url = `${API_BASE_URL}/api/v1/widgets/${widgetId}/conversation`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    });
    // Parse defensively (a 502 from the ingress is HTML, not JSON) and route errors
    // through buildAndThrowApiError so a 401 clears the session like every other call
    // and the thrown error carries .status + .responseData (review on #182).
    let responseData;
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      try {
        responseData = await response.json();
      } catch {
        responseData = { detail: 'Invalid JSON response' };
      }
    } else {
      const text = await response.text();
      responseData = { detail: text || 'Unexpected response format' };
    }
    if (!response.ok) {
      buildAndThrowApiError(response, responseData, 'Could not open conversation', 'openWidgetConversation');
    }
    return responseData;
  },


  async logout() {
    const url = `${API_BASE_URL}/logout`;
    const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
    logRequest('POST', url, headers, null);

    try {
      const response = await fetch(url, { method: 'POST', headers });
      const responseData = await response.json();
      logResponse(response, responseData);
      return responseData;
    } catch (error) {
      logError(error, 'logout');
      return { message: 'Logged out locally' };
    }
  },

  /**
   * List all organizations (super-admin only).
   * @returns {Promise<Array>} List of org objects
   */
  async getAdminOrgs() {
    const url = `${API_BASE_URL}/admin/orgs`;
    const headers = { ...getAuthHeaders() };
    logRequest('GET', url, headers, null);
    try {
      const response = await fetch(url, { method: 'GET', headers });
      const responseData = await response.json();
      logResponse(response, responseData);
      if (!response.ok) {
        buildAndThrowApiError(response, responseData, 'Failed to fetch organizations', 'getAdminOrgs');
      }
      return responseData;
    } catch (error) {
      throwIfNetworkError(error, 'getAdminOrgs');
      logError(error, 'getAdminOrgs');
      throw error;
    }
  },

  /**
   * Get notifications for the current user
   * @param {Object} options - { unreadOnly: boolean, limit: number }
   * @returns {Promise<Array>} List of notifications
   */
  async getNotifications(options = {}) {
    const { unreadOnly = false, limit = 50 } = options;
    const url = new URL(`${API_BASE_URL}/api/v1/notifications`);
    url.searchParams.append('unread_only', unreadOnly);
    url.searchParams.append('limit', limit);
    const headers = { ...getAuthHeaders() };
    logRequest('GET', url.toString(), headers, null);
    try {
      const response = await fetch(url.toString(), { method: 'GET', headers });
      const responseData = await response.json();
      logResponse(response, responseData);
      if (!response.ok) {
        buildAndThrowApiError(response, responseData, 'Failed to fetch notifications', 'getNotifications');
      }
      return responseData;
    } catch (error) {
      throwIfNetworkError(error, 'getNotifications');
      logError(error, 'getNotifications');
      throw error;
    }
  },

  /**
   * Mark a notification as read
   * @param {string} notificationId - Notification UUID
   * @returns {Promise<Object>} Updated notification
   */
  async markNotificationRead(notificationId) {
    const url = `${API_BASE_URL}/api/v1/notifications/${notificationId}/read`;
    const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
    logRequest('PATCH', url, headers, null);
    try {
      const response = await fetch(url, { method: 'PATCH', headers });
      const responseData = await response.json();
      logResponse(response, responseData);
      if (!response.ok) {
        buildAndThrowApiError(response, responseData, 'Failed to mark notification read', 'markNotificationRead');
      }
      return responseData;
    } catch (error) {
      throwIfNetworkError(error, 'markNotificationRead');
      logError(error, 'markNotificationRead');
      throw error;
    }
  },

  /**
   * Mark all notifications as read for the current user
   * @returns {Promise<Object>}
   */
  async markAllNotificationsRead() {
    const url = `${API_BASE_URL}/api/v1/notifications/read-all`;
    const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
    logRequest('PATCH', url, headers, null);
    try {
      const response = await fetch(url, { method: 'PATCH', headers });
      const responseData = await response.json();
      logResponse(response, responseData);
      if (!response.ok) {
        buildAndThrowApiError(response, responseData, 'Failed to mark all read', 'markAllNotificationsRead');
      }
      return responseData;
    } catch (error) {
      throwIfNetworkError(error, 'markAllNotificationsRead');
      logError(error, 'markAllNotificationsRead');
      throw error;
    }
  },

  /**
   * Get pending HITL review for a CSV ingestion job
   * @param {string} jobId - Job ID
   * @returns {Promise<Object>} Pending review payload
   */
  async getCsvPendingReview(jobId) {
    const url = `${API_BASE_URL}/api/v1/internal/csv-ingestion-jobs/${jobId}/pending-review`;
    const headers = {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    };

    logRequest('GET', url, headers, null);

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers,
      });

      let responseData;
      const contentType = response.headers.get('content-type');

      if (contentType && contentType.includes('application/json')) {
        responseData = await response.json();
        logResponse(response, responseData);
      } else {
        const text = await response.text();
        responseData = { detail: text || 'Unexpected response format' };
      }

      if (!response.ok) {
        buildAndThrowApiError(response, responseData, 'Failed to get pending review', 'getCsvPendingReview');
      }

      return responseData;
    } catch (error) {
      throwIfNetworkError(error, 'getCsvPendingReview');
      logError(error, 'getCsvPendingReview - General Error');
      throw error;
    }
  },

  /**
   * Submit HITL review for a CSV ingestion job
   * @param {string} jobId - Job ID
   * @param {Object} reviewData - Review submission data
   * @returns {Promise<Object>} Submission response
   */
  async submitCsvReview(jobId, reviewData) {
    const url = `${API_BASE_URL}/api/v1/internal/csv-ingestion-jobs/${jobId}/submit-review`;
    const headers = {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    };

    logRequest('POST', url, headers, reviewData);

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(reviewData),
      });

      let responseData;
      const contentType = response.headers.get('content-type');

      if (contentType && contentType.includes('application/json')) {
        responseData = await response.json();
        logResponse(response, responseData);
      } else {
        const text = await response.text();
        responseData = { detail: text || 'Unexpected response format' };
      }

      if (!response.ok) {
        buildAndThrowApiError(response, responseData, 'Failed to submit review', 'submitCsvReview');
      }

      return responseData;
    } catch (error) {
      throwIfNetworkError(error, 'submitCsvReview');
      logError(error, 'submitCsvReview - General Error');
      throw error;
    }
  },

  /**
   * Fetch a dataset file as an object URL for the document review viewer.
   *
   * Separate from fetchFileImageObjectUrl (which ReviewMediaPanel uses for
   * images) because a PDF has one extra requirement: the blob's MIME type must
   * be `application/pdf` or Chrome refuses to render it in an iframe and shows
   * an empty black frame. `response.blob()` takes its type verbatim from the
   * response Content-Type, so a `/content` endpoint serving
   * `application/octet-stream` produces an unrenderable blob. Re-wrap with the
   * type inferred from the filename when the server didn't give us a usable one.
   *
   * @param {string} datasetId
   * @param {string} fileId
   * @param {string} [fileName] used to infer the type when the response is generic
   * @returns {Promise<{url: string, contentType: string}|null>} null on failure
   */
  async fetchFileContentObjectUrl(datasetId, fileId, fileName = '') {
    const url = `${API_BASE_URL}/api/v1/datasets/${datasetId}/files/${fileId}/content`;
    const headers = { ...getAuthHeaders() };
    try {
      const response = await fetch(url, { method: 'GET', headers });
      if (!response.ok) return null;

      const blob = await response.blob();
      const served = (blob.type || '').toLowerCase();
      const ext = String(fileName).toLowerCase().split('.').pop();
      const BY_EXT = {
        pdf: 'application/pdf',
        png: 'image/png',
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        webp: 'image/webp',
        gif: 'image/gif',
      };
      const inferred = BY_EXT[ext] || '';

      // Trust the server only when it said something specific and useful.
      const usable = served && served !== 'application/octet-stream' && served !== 'binary/octet-stream';
      const contentType = usable ? served : inferred;

      const typed = (!usable && inferred)
        ? new Blob([blob], { type: inferred })
        : blob;

      return { url: URL.createObjectURL(typed), contentType };
    } catch (error) {
      logError(error, 'fetchFileContentObjectUrl - General Error');
      return null;
    }
  },

  /**
   * Document field review (HITL) — discovery. Lists the documents in a dataset
   * that are parked awaiting review, with their flagged fields.
   *
   * This dataset-scoped read exists because the per-document endpoints are keyed
   * by `document_id` while the dataset file list returns `file_id` — without it
   * the client cannot construct the per-document call at all.
   *
   * Cursor-paginated: pass `next_cursor` from the previous response to continue;
   * a null `next_cursor` means this was the last page. Page size is capped at
   * 200 server-side.
   *
   * The cursor is OPAQUE (a composite of created_at and document_id). Echo it
   * back verbatim — never build one from a document_id and never parse it. A
   * stale or malformed cursor returns 422, whose only valid recovery is
   * refetching the first page.
   *
   * Results come back in upload order (created_at). Do NOT re-sort client-side.
   *
   * A flag is identified by `entity_id` (UUID), NOT by `field_id` — entity type
   * is not unique within a document (one real form produced `table_1` three
   * times). `field_id` is a display label and may repeat.
   *
   * @param {string} datasetId
   * @param {{limit?: number, cursor?: string|null}} [opts]
   * @returns {Promise<Object>} { dataset_id, floor, documents: [{ document_id,
   *   file_id, file_name, pending_since, flags: [{ entity_id, field_id,
   *   row_label, extracted_value, confidence, evidence, reasons }] }],
   *   next_cursor }
   *
   * `pending_since` may be null — the marker table and the lifecycle live in
   * different databases and can disagree.
   *
   * `flags` may be EMPTY on a parked document: a scan too degraded for OCR to
   * read parks on a document-level tripwire, with no field ever measured.
   */
  async getDatasetPendingReview(datasetId, { limit = 50, cursor = null } = {}) {
    const params = new URLSearchParams({ limit: String(limit) });
    if (cursor) params.set('cursor', cursor);
    const url = `${API_BASE_URL}/api/v1/datasets/${datasetId}/pending-review?${params}`;
    const headers = { 'Content-Type': 'application/json', ...getAuthHeaders() };
    logRequest('GET', url, headers, null);

    try {
      const response = await fetch(url, { method: 'GET', headers });
      let responseData;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        responseData = await response.json();
        logResponse(response, responseData);
      } else {
        const text = await response.text();
        responseData = { detail: text || 'Unexpected response format' };
      }
      if (!response.ok) {
        buildAndThrowApiError(response, responseData, 'Failed to get pending review', 'getDatasetPendingReview');
      }
      return responseData;
    } catch (error) {
      throwIfNetworkError(error, 'getDatasetPendingReview');
      logError(error, 'getDatasetPendingReview - General Error');
      throw error;
    }
  },

  /**
   * Document field review (HITL) — one document's current flags. Used to refresh
   * a single document after a 409 without refetching the whole dataset.
   * Unauthorized returns 404, not 403 — existence is deliberately not leaked.
   * @param {string} documentId
   * @returns {Promise<Object>} { document_id, file_id, dataset_id, floor, flags: [...] }
   */
  async getDocumentPendingReview(documentId) {
    const url = `${API_BASE_URL}/api/v1/documents/${documentId}/pending-review`;
    const headers = { 'Content-Type': 'application/json', ...getAuthHeaders() };
    logRequest('GET', url, headers, null);

    try {
      const response = await fetch(url, { method: 'GET', headers });
      let responseData;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        responseData = await response.json();
        logResponse(response, responseData);
      } else {
        const text = await response.text();
        responseData = { detail: text || 'Unexpected response format' };
      }
      if (!response.ok) {
        buildAndThrowApiError(response, responseData, 'Failed to get document review', 'getDocumentPendingReview');
      }
      return responseData;
    } catch (error) {
      throwIfNetworkError(error, 'getDocumentPendingReview');
      logError(error, 'getDocumentPendingReview - General Error');
      throw error;
    }
  },

  /**
   * Document field review (HITL) — resolve one document. The server recomputes
   * the flag set and rejects an incomplete submission, so every currently
   * flagged entity must appear in `resolutions`.
   *
   * The release unit is the DOCUMENT, not the dataset: each call is atomic for
   * its own document and partial progress across a dataset is intended.
   *
   * A submit does NOT always release: derived fields (age, length-of-stay)
   * inherit min(input confidence), so skipping a base field can newly flag a
   * field derived from it. Expect `released: false` with `remaining_flags`.
   *
   * @param {string} documentId
   * @param {Array<{entity_id: string, field_id: string, action: 'confirm'|'correct'|'skip', corrected_value?: string}>} resolutions
   * @returns {Promise<Object>} { document_id, document_status, released,
   *   counts: {confirmed, corrected, skipped}, remaining_flags: [...],
   *   refreshed: {derivations, facts_push_ok, facts_push_impossible, graph} }
   *
   * `facts_push_ok` means the push ran without raising, NOT that a facts row was
   * written — the older `facts_table` name claimed the latter.
   */
  async submitDocumentReview(documentId, resolutions) {
    const url = `${API_BASE_URL}/api/v1/documents/${documentId}/review/resolve`;
    const headers = { 'Content-Type': 'application/json', ...getAuthHeaders() };
    const body = { resolutions };
    logRequest('POST', url, headers, body);

    try {
      const response = await fetch(url, { method: 'POST', headers, body: JSON.stringify(body) });
      let responseData;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        responseData = await response.json();
        logResponse(response, responseData);
      } else {
        const text = await response.text();
        responseData = { detail: text || 'Unexpected response format' };
      }
      if (!response.ok) {
        buildAndThrowApiError(response, responseData, 'Failed to submit document review', 'submitDocumentReview');
      }
      return responseData;
    } catch (error) {
      throwIfNetworkError(error, 'submitDocumentReview');
      logError(error, 'submitDocumentReview - General Error');
      throw error;
    }
  },

  /**
   * Get the transcript for a voice-recorded dataset (PRD-941).
   * @param {string} datasetId - Dataset ID
   * @returns {Promise<Object>} { dataset_id, status, segments: [{ id, start_ms, end_ms, text }], full_text }
   */
  async getDatasetTranscript(datasetId) {
    const url = `${API_BASE_URL}/api/v1/datasets/${datasetId}/transcript`;
    const headers = { 'Content-Type': 'application/json', ...getAuthHeaders() };
    logRequest('GET', url, headers, null);

    try {
      const response = await fetch(url, { method: 'GET', headers });
      let responseData;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        responseData = await response.json();
        logResponse(response, responseData);
      } else {
        const text = await response.text();
        responseData = { detail: text || 'Unexpected response format' };
      }
      if (!response.ok) {
        buildAndThrowApiError(response, responseData, 'Failed to get transcript', 'getDatasetTranscript');
      }
      return responseData;
    } catch (error) {
      throwIfNetworkError(error, 'getDatasetTranscript');
      logError(error, 'getDatasetTranscript - General Error');
      throw error;
    }
  },

  /**
   * Reprocess the stored audio for a failed voice dataset (PRD-941) — no re-record needed.
   * @param {string} datasetId - Dataset ID
   * @returns {Promise<Object>} { job_status } — or throws with .status 409 if not retryable
   */
  async reprocessDataset(datasetId) {
    const url = `${API_BASE_URL}/api/v1/datasets/${datasetId}/reprocess`;
    const headers = { 'Content-Type': 'application/json', ...getAuthHeaders() };
    logRequest('POST', url, headers, null);

    try {
      const response = await fetch(url, { method: 'POST', headers });
      let responseData;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        responseData = await response.json();
        logResponse(response, responseData);
      } else {
        const text = await response.text();
        responseData = { detail: text || 'Unexpected response format' };
      }
      if (!response.ok) {
        buildAndThrowApiError(response, responseData, 'Failed to reprocess dataset', 'reprocessDataset');
      }
      return responseData;
    } catch (error) {
      throwIfNetworkError(error, 'reprocessDataset');
      logError(error, 'reprocessDataset - General Error');
      throw error;
    }
  },

  // PRD-826 push-to-BI: shared request helper, used only by push methods below.
  // Keeps the new surface compact while matching the same error/logging contract.
  async _pushRequest(method, path, body, context) {
    const url = `${API_BASE_URL}${path}`;
    const headers = { ...getAuthHeaders() };
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    logRequest(method, url, headers, body);
    try {
      const init = { method, headers };
      if (body !== undefined) init.body = JSON.stringify(body);
      const response = await fetch(url, init);
      let data;
      const contentType = response.headers.get('content-type') || '';
      if (response.status === 204) {
        data = null;
      } else if (contentType.includes('application/json')) {
        data = await response.json();
      } else {
        const text = await response.text();
        data = text ? { detail: text } : null;
      }
      logResponse(response, data);
      if (!response.ok) buildAndThrowApiError(response, data, `Failed ${context}`, context);
      return data;
    } catch (error) {
      throwIfNetworkError(error, context);
      logError(error, context);
      throw error;
    }
  },

  // ── Push connections ─────────────────────────────────────────────────────
  createPushConnection(payload) {
    return apiService._pushRequest('POST', '/api/v1/push-connections', payload, 'createPushConnection');
  },
  listPushConnections() {
    return apiService._pushRequest('GET', '/api/v1/push-connections', undefined, 'listPushConnections');
  },
  testPushConnection(connectionId) {
    return apiService._pushRequest('POST', `/api/v1/push-connections/${connectionId}/test`, {}, 'testPushConnection');
  },

  // ── Push automations ─────────────────────────────────────────────────────
  createPushAutomation(payload) {
    return apiService._pushRequest('POST', '/api/v1/push-automations', payload, 'createPushAutomation');
  },
  listPushAutomations(params = {}) {
    const qs = new URLSearchParams();
    if (params.page) qs.set('page', params.page);
    if (params.page_size) qs.set('page_size', params.page_size);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return apiService._pushRequest('GET', `/api/v1/push-automations${suffix}`, undefined, 'listPushAutomations');
  },
  previewPushAutomation(automationId, payload = {}) {
    return apiService._pushRequest('POST', `/api/v1/push-automations/${automationId}/preview`, payload, 'previewPushAutomation');
  },
  runPushAutomationNow(automationId) {
    return apiService._pushRequest('POST', `/api/v1/push-automations/${automationId}/run`, {}, 'runPushAutomationNow');
  },
  patchPushAutomation(automationId, payload) {
    return apiService._pushRequest('PATCH', `/api/v1/push-automations/${automationId}`, payload, 'patchPushAutomation');
  },
  deletePushAutomation(automationId) {
    return apiService._pushRequest('DELETE', `/api/v1/push-automations/${automationId}`, undefined, 'deletePushAutomation');
  },
  pausePushAutomation(automationId) {
    return apiService._pushRequest('POST', `/api/v1/push-automations/${automationId}/pause`, {}, 'pausePushAutomation');
  },
  resumePushAutomation(automationId) {
    return apiService._pushRequest('POST', `/api/v1/push-automations/${automationId}/resume`, {}, 'resumePushAutomation');
  },

  // ── Push runs (history surface §6.3) ─────────────────────────────────────
  listPushRuns(automationId, params = {}) {
    const qs = new URLSearchParams();
    if (params.page) qs.set('page', params.page);
    if (params.page_size) qs.set('page_size', params.page_size);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return apiService._pushRequest('GET', `/api/v1/push-automations/${automationId}/runs${suffix}`, undefined, 'listPushRuns');
  },
  getPushRun(runId) {
    return apiService._pushRequest('GET', `/api/v1/push-runs/${runId}`, undefined, 'getPushRun');
  },
  cancelPushRun(runId) {
    return apiService._pushRequest('POST', `/api/v1/push-runs/${runId}/cancel`, {}, 'cancelPushRun');
  },

  // ── Ingest-automation run history (PRD-810 backend, PRD-811 drawer) ──────
  listIngestRuns(automationId, params = {}) {
    const qs = new URLSearchParams();
    if (params.page) qs.set('page', params.page);
    if (params.page_size) qs.set('page_size', params.page_size);
    if (params.from_date) qs.set('from_date', params.from_date);
    if (params.to_date) qs.set('to_date', params.to_date);
    if (params.user_id) qs.set('user_id', params.user_id);
    if (params.trigger_type) qs.set('trigger_type', params.trigger_type);
    if (params.status) qs.set('status', params.status);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return apiService._pushRequest(
      'GET',
      `/api/v1/ingest-automations/${automationId}/runs${suffix}`,
      undefined,
      'listIngestRuns',
    );
  },
  getIngestRun(automationId, runId) {
    return apiService._pushRequest(
      'GET',
      `/api/v1/ingest-automations/${automationId}/runs/${runId}`,
      undefined,
      'getIngestRun',
    );
  },

  // ── Module framework + SOP Library ──────────────────────────────────────

  /** Enabled modules for the caller's org (chat footer / upload dropdown).
   *  Any authenticated user; only returns rows where `disabled=false`. */
  async getMyModules() {
    const url = `${API_BASE_URL}/api/v1/me/modules`;
    const headers = { ...getAuthHeaders() };
    logRequest('GET', url, headers, null);
    try {
      const response = await fetch(url, { method: 'GET', headers });
      const data = await response.json();
      logResponse(response, data);
      if (!response.ok) buildAndThrowApiError(response, data, 'Failed to load modules', 'getMyModules');
      return data;
    } catch (error) {
      throwIfNetworkError(error, 'getMyModules');
      logError(error, 'getMyModules - General Error');
      throw error;
    }
  },

  /** List platform modules + this org's on/off state for each. Admin only. */
  async getOrgModules(orgId) {
    const url = `${API_BASE_URL}/api/v1/orgs/${orgId}/modules`;
    const headers = { ...getAuthHeaders() };
    logRequest('GET', url, headers, null);
    try {
      const response = await fetch(url, { method: 'GET', headers });
      const data = await response.json();
      logResponse(response, data);
      if (!response.ok) buildAndThrowApiError(response, data, 'Failed to list modules', 'getOrgModules');
      return data;
    } catch (error) {
      throwIfNetworkError(error, 'getOrgModules');
      logError(error, 'getOrgModules - General Error');
      throw error;
    }
  },

  /** Enable or disable a module for an org. Admin only. */
  async setOrgModule(orgId, moduleId, enabled) {
    const url = `${API_BASE_URL}/api/v1/orgs/${orgId}/modules/${moduleId}`;
    const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
    const body = JSON.stringify({ enabled: !!enabled });
    logRequest('PUT', url, headers, body);
    try {
      const response = await fetch(url, { method: 'PUT', headers, body });
      const data = await response.json();
      logResponse(response, data);
      if (!response.ok) buildAndThrowApiError(response, data, 'Failed to toggle module', 'setOrgModule');
      return data;
    } catch (error) {
      throwIfNetworkError(error, 'setOrgModule');
      logError(error, 'setOrgModule - General Error');
      throw error;
    }
  },

  /**
   * Per-file fan-out upload to a module. Each file becomes its own dataset
   * with `module_id` set. Returns `{module_id, results: [{file_name,
   * dataset_id?, file?, error?}, ...]}` — partial-success per PRD §Story 2.
   */
  async uploadFilesToModule(moduleId, files) {
    const url = `${API_BASE_URL}/api/v1/modules/${moduleId}/datasets`;
    const headers = { ...getAuthHeaders() }; // no Content-Type — let browser set multipart boundary
    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    logRequest('POST', url, headers, `<multipart with ${files.length} file(s)>`);
    try {
      const response = await fetch(url, { method: 'POST', headers, body: formData });
      const data = await response.json();
      logResponse(response, data);
      if (!response.ok) buildAndThrowApiError(response, data, 'Failed to upload to module', 'uploadFilesToModule');
      return data;
    } catch (error) {
      throwIfNetworkError(error, 'uploadFilesToModule');
      logError(error, 'uploadFilesToModule - General Error');
      throw error;
    }
  },

  /** Current user's sticky-selected module datasets. Stale rows already
   *  filtered by the server, so caller can render dataset_ids directly. */
  async getMyModuleSelections() {
    const url = `${API_BASE_URL}/api/v1/me/module-selections`;
    const headers = { ...getAuthHeaders() };
    logRequest('GET', url, headers, null);
    try {
      const response = await fetch(url, { method: 'GET', headers });
      const data = await response.json();
      logResponse(response, data);
      if (!response.ok) buildAndThrowApiError(response, data, 'Failed to load selections', 'getMyModuleSelections');
      return data;
    } catch (error) {
      throwIfNetworkError(error, 'getMyModuleSelections');
      logError(error, 'getMyModuleSelections - General Error');
      throw error;
    }
  },

  /** Replace the caller's sticky selections with the supplied list.
   *  Server silently drops invalid ids (other-org / not module-owned /
   *  disabled module / deleted) and returns the canonical persisted set. */
  async setMyModuleSelections(datasetIds) {
    const url = `${API_BASE_URL}/api/v1/me/module-selections`;
    const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
    const body = JSON.stringify({ dataset_ids: datasetIds || [] });
    logRequest('PUT', url, headers, body);
    try {
      const response = await fetch(url, { method: 'PUT', headers, body });
      const data = await response.json();
      logResponse(response, data);
      if (!response.ok) buildAndThrowApiError(response, data, 'Failed to save selections', 'setMyModuleSelections');
      return data;
    } catch (error) {
      throwIfNetworkError(error, 'setMyModuleSelections');
      logError(error, 'setMyModuleSelections - General Error');
      throw error;
    }
  },

  /**
   * List authoring scopes (super-admin only).
   */
  async getContextScopes() {
    const url = `${API_BASE_URL}/api/v1/context/authoring/scopes`;
    const headers = { ...getAuthHeaders() };
    logRequest('GET', url, headers, null);
    try {
      const response = await fetch(url, { method: 'GET', headers });
      const data = await response.json();
      logResponse(response, data);
      if (!response.ok) buildAndThrowApiError(response, data, 'Failed to load scopes', 'getContextScopes');
      return data;
    } catch (error) {
      throwIfNetworkError(error, 'getContextScopes');
      logError(error, 'getContextScopes');
      throw error;
    }
  },

  /**
   * The org's published org-wide chat fragment: the text that grounds every question in the org
   * that no domain bundle serves. Returns { org_id, chat_fragment, version }.
   */
  async getOrgWideChat(orgId) {
    const url = new URL(`${API_BASE_URL}/api/v1/context/authoring/org-chat`);
    url.searchParams.append('org_id', orgId);
    const headers = { ...getAuthHeaders() };
    logRequest('GET', url.toString(), headers, null);
    try {
      const response = await fetch(url.toString(), { method: 'GET', headers });
      const data = await response.json();
      logResponse(response, data);
      if (!response.ok) buildAndThrowApiError(response, data, 'Failed to load org-wide chat', 'getOrgWideChat');
      return data;
    } catch (error) {
      throwIfNetworkError(error, 'getOrgWideChat');
      logError(error, 'getOrgWideChat');
      throw error;
    }
  },

  /**
   * Get effective layers + org candidate rows for a scope.
   */
  async getContextLayers(domain, orgId, pathway) {
    const url = new URL(`${API_BASE_URL}/api/v1/context/authoring/layers`);
    url.searchParams.append('domain', domain);
    url.searchParams.append('org_id', orgId);
    if (pathway) url.searchParams.append('pathway', pathway);
    const headers = { ...getAuthHeaders() };
    logRequest('GET', url.toString(), headers, null);
    try {
      const response = await fetch(url.toString(), { method: 'GET', headers });
      const data = await response.json();
      logResponse(response, data);
      if (!response.ok) buildAndThrowApiError(response, data, 'Failed to load layers', 'getContextLayers');
      return data;
    } catch (error) {
      throwIfNetworkError(error, 'getContextLayers');
      logError(error, 'getContextLayers');
      throw error;
    }
  },

  /**
   * List context model versions + production pointer for a scope.
   */
  async getContextVersions(scopeKey) {
    const url = new URL(`${API_BASE_URL}/api/v1/context/authoring/versions`);
    url.searchParams.append('scope_key', scopeKey);
    const headers = { ...getAuthHeaders() };
    logRequest('GET', url.toString(), headers, null);
    try {
      const response = await fetch(url.toString(), { method: 'GET', headers });
      const data = await response.json();
      logResponse(response, data);
      if (!response.ok) buildAndThrowApiError(response, data, 'Failed to load versions', 'getContextVersions');
      return data;
    } catch (error) {
      throwIfNetworkError(error, 'getContextVersions');
      logError(error, 'getContextVersions');
      throw error;
    }
  },

  /**
   * Eval-run history for one scope, newest first. Distinct from getContextVersions: a version keeps
   * only its latest score, so re-evaluating a candidate overwrites it — this is the durable log of
   * every run, with the params that produced each one.
   */
  async getContextEvalRuns(scopeKey, limit = 20) {
    const url = new URL(`${API_BASE_URL}/api/v1/context/authoring/candidates/runs`);
    url.searchParams.append('scope_key', scopeKey);
    url.searchParams.append('limit', String(limit));
    const headers = { ...getAuthHeaders() };
    logRequest('GET', url.toString(), headers, null);
    try {
      const response = await fetch(url.toString(), { method: 'GET', headers });
      const data = await response.json();
      logResponse(response, data);
      if (!response.ok) buildAndThrowApiError(response, data, 'Failed to load eval runs', 'getContextEvalRuns');
      return data;
    } catch (error) {
      throwIfNetworkError(error, 'getContextEvalRuns');
      logError(error, 'getContextEvalRuns');
      throw error;
    }
  },

  /**
   * Which of this org's overrides are eligible to move up a tier, with the value each would fall
   * back to if promoted.
   */
  async getHarvestCandidates(domain, orgId, pathway) {
    const url = new URL(`${API_BASE_URL}/api/v1/context/authoring/layers/harvest-candidates`);
    url.searchParams.append('domain', domain);
    url.searchParams.append('org_id', orgId);
    if (pathway) url.searchParams.append('pathway', pathway);
    const headers = { ...getAuthHeaders() };
    logRequest('GET', url.toString(), headers, null);
    try {
      const response = await fetch(url.toString(), { method: 'GET', headers });
      const data = await response.json();
      logResponse(response, data);
      if (!response.ok) buildAndThrowApiError(response, data, 'Failed to load harvest candidates', 'getHarvestCandidates');
      return data;
    } catch (error) {
      throwIfNetworkError(error, 'getHarvestCandidates');
      logError(error, 'getHarvestCandidates');
      throw error;
    }
  },

  /**
   * Move an org-authored attribute UP to the pathway or domain tier. This changes the DEFAULT every
   * other org in the scope inherits, so it is a super-admin action and audited server-side.
   */
  async harvestContextLayers(orgId, domain, pathway, targetTier, items) {
    const url = `${API_BASE_URL}/api/v1/context/authoring/layers/harvest`;
    const headers = { 'Content-Type': 'application/json', ...getAuthHeaders() };
    const body = { org_id: orgId, domain, pathway: pathway || null, target_tier: targetTier, items };
    logRequest('POST', url, headers, body);
    try {
      const response = await fetch(url, { method: 'POST', headers, body: JSON.stringify(body) });
      const data = await response.json();
      logResponse(response, data);
      if (!response.ok) buildAndThrowApiError(response, data, 'Failed to harvest', 'harvestContextLayers');
      return data;
    } catch (error) {
      throwIfNetworkError(error, 'harvestContextLayers');
      logError(error, 'harvestContextLayers');
      throw error;
    }
  },

  /**
   * Save org-layer field edits (and optional chat fragment) as candidate rows.
   */
  async saveContextLayers(orgId, fields, chatFragment, groupPrompts) {
    const url = `${API_BASE_URL}/api/v1/context/authoring/layers/save`;
    const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
    const body = JSON.stringify({
      org_id: orgId,
      fields,
      chat_fragment: chatFragment ?? null,
      group_prompts: groupPrompts && Object.keys(groupPrompts).length ? groupPrompts : null,
    });
    logRequest('POST', url, headers, body);
    try {
      const response = await fetch(url, { method: 'POST', headers, body });
      const data = await response.json();
      logResponse(response, data);
      if (!response.ok) buildAndThrowApiError(response, data, 'Failed to save layers', 'saveContextLayers');
      return data;
    } catch (error) {
      throwIfNetworkError(error, 'saveContextLayers');
      logError(error, 'saveContextLayers');
      throw error;
    }
  },

  /**
   * Compile a candidate context model for a scope.
   */
  async compileContextCandidate(domain, pathway, orgId) {
    const url = `${API_BASE_URL}/api/v1/context/authoring/candidates/compile`;
    const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
    const body = JSON.stringify({ domain, pathway: pathway || null, org_id: orgId });
    logRequest('POST', url, headers, body);
    try {
      const response = await fetch(url, { method: 'POST', headers, body });
      const data = await response.json();
      logResponse(response, data);
      if (!response.ok) buildAndThrowApiError(response, data, 'Failed to compile candidate', 'compileContextCandidate');
      return data;
    } catch (error) {
      throwIfNetworkError(error, 'compileContextCandidate');
      logError(error, 'compileContextCandidate');
      throw error;
    }
  },

  /**
   * Kick off an eval run for a candidate version (202 = accepted, poll impact).
   */
  async evaluateContextCandidate(scopeKey, candidateVersion, bundleId, datasetId) {
    const url = `${API_BASE_URL}/api/v1/context/authoring/candidates/evaluate`;
    const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
    const body = JSON.stringify({
      scope_key: scopeKey,
      candidate_version: candidateVersion,
      bundle_id: bundleId,
      dataset_id: datasetId,
    });
    logRequest('POST', url, headers, body);
    try {
      const response = await fetch(url, { method: 'POST', headers, body });
      const data = await response.json();
      logResponse(response, data);
      if (!response.ok) buildAndThrowApiError(response, data, 'Failed to start eval', 'evaluateContextCandidate');
      return data;
    } catch (error) {
      throwIfNetworkError(error, 'evaluateContextCandidate');
      logError(error, 'evaluateContextCandidate');
      throw error;
    }
  },

  /**
   * Impact report for a candidate version (status: running|done|failed|not_evaluated).
   */
  async getContextCandidateImpact(candidateVersion, scopeKey) {
    const url = new URL(`${API_BASE_URL}/api/v1/context/authoring/candidates/${encodeURIComponent(candidateVersion)}/impact`);
    url.searchParams.append('scope_key', scopeKey);
    const headers = { ...getAuthHeaders() };
    logRequest('GET', url.toString(), headers, null);
    try {
      const response = await fetch(url.toString(), { method: 'GET', headers });
      const data = await response.json();
      logResponse(response, data);
      if (!response.ok) buildAndThrowApiError(response, data, 'Failed to load impact', 'getContextCandidateImpact');
      return data;
    } catch (error) {
      throwIfNetworkError(error, 'getContextCandidateImpact');
      logError(error, 'getContextCandidateImpact');
      throw error;
    }
  },

  /**
   * Discard draft (candidate) layer edits: named slots, or ALL drafts when fields is null.
   */
  async discardContextLayers(orgId, fields) {
    const url = `${API_BASE_URL}/api/v1/context/authoring/layers/discard`;
    const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
    const body = JSON.stringify({ org_id: orgId, fields: fields && fields.length ? fields : null });
    logRequest('POST', url, headers, body);
    try {
      const response = await fetch(url, { method: 'POST', headers, body });
      const data = await response.json();
      logResponse(response, data);
      if (!response.ok) buildAndThrowApiError(response, data, 'Failed to discard drafts', 'discardContextLayers');
      return data;
    } catch (error) {
      throwIfNetworkError(error, 'discardContextLayers');
      logError(error, 'discardContextLayers');
      throw error;
    }
  },

  /**
   * Chat-only publish: always the org-level (pathway-less) bundle Chat actually reads.
   * Pathway argument is ignored by the server.
   */
  async publishContextChatUpdate(domain, pathway, orgId) {
    const url = `${API_BASE_URL}/api/v1/context/authoring/publish-chat`;
    const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
    const body = JSON.stringify({ domain, pathway: pathway || null, org_id: orgId });
    logRequest('POST', url, headers, body);
    try {
      const response = await fetch(url, { method: 'POST', headers, body });
      const data = await response.json();
      logResponse(response, data);
      if (!response.ok) buildAndThrowApiError(response, data, 'Failed to publish chat update', 'publishContextChatUpdate');
      return data;
    } catch (error) {
      throwIfNetworkError(error, 'publishContextChatUpdate');
      logError(error, 'publishContextChatUpdate');
      throw error;
    }
  },

  /**
   * Publish (promote) a candidate version to production for a scope.
   */
  async publishContextCandidate(scopeKey, candidateVersion) {
    const url = `${API_BASE_URL}/api/v1/context/authoring/publish`;
    const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
    const body = JSON.stringify({ scope_key: scopeKey, candidate_version: candidateVersion });
    logRequest('POST', url, headers, body);
    try {
      const response = await fetch(url, { method: 'POST', headers, body });
      const data = await response.json();
      logResponse(response, data);
      if (!response.ok) buildAndThrowApiError(response, data, 'Failed to publish candidate', 'publishContextCandidate');
      return data;
    } catch (error) {
      throwIfNetworkError(error, 'publishContextCandidate');
      logError(error, 'publishContextCandidate');
      throw error;
    }
  },

  /**
   * Roll the production pointer back to the previous version for a scope.
   */
  async rollbackContextScope(scopeKey) {
    const url = `${API_BASE_URL}/api/v1/context/authoring/rollback`;
    const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
    const body = JSON.stringify({ scope_key: scopeKey });
    logRequest('POST', url, headers, body);
    try {
      const response = await fetch(url, { method: 'POST', headers, body });
      const data = await response.json();
      logResponse(response, data);
      if (!response.ok) buildAndThrowApiError(response, data, 'Failed to rollback', 'rollbackContextScope');
      return data;
    } catch (error) {
      throwIfNetworkError(error, 'rollbackContextScope');
      logError(error, 'rollbackContextScope');
      throw error;
    }
  },

  /**
   * List available eval packs (bundle + dataset pairs) for context authoring.
   */
  async getEvalPacks() {
    const url = `${API_BASE_URL}/api/v1/context/authoring/eval-packs`;
    const headers = { ...getAuthHeaders() };
    logRequest('GET', url, headers, null);
    try {
      const response = await fetch(url, { method: 'GET', headers });
      const data = await response.json();
      logResponse(response, data);
      if (!response.ok) buildAndThrowApiError(response, data, 'Failed to load eval packs', 'getEvalPacks');
      return data;
    } catch (error) {
      throwIfNetworkError(error, 'getEvalPacks');
      logError(error, 'getEvalPacks');
      throw error;
    }
  },

  // Upload a new eval pack: the input documents plus the ground-truth CSV. Manifest digests and
  // validation are server-side, so a rejected pack returns the precise reason and writes nothing.
  // No client-side size cap — App Gateway WAF / nginx enforce the body limit and return 413
  // (often as HTML, so json() below can fail). The authoring UI tells the user to split the pack.
  async createEvalPack(bundleId, groundTruthFile, inputFiles) {
    const url = `${API_BASE_URL}/api/v1/context/authoring/eval-packs/${encodeURIComponent(bundleId)}`;
    // No Content-Type header — the browser must set it so the multipart boundary is correct.
    const headers = { ...getAuthHeaders() };
    const form = new FormData();
    form.append('ground_truth', groundTruthFile);
    const files = Array.from(inputFiles || []);
    files.forEach((f) => form.append('inputs', f));
    logRequest('POST', url, headers, `FormData with ${files.length} input(s)`);
    try {
      const response = await fetch(url, { method: 'POST', headers, body: form });
      const data = await response.json().catch(() => ({}));
      logResponse(response, data);
      if (response.status === 413) {
        const error = new Error('Upload too large for the gateway (413). Split the pack and retry.');
        error.status = 413;
        logError(error, 'createEvalPack');
        throw error;
      }
      if (!response.ok) buildAndThrowApiError(response, data, 'Failed to create eval pack', 'createEvalPack');
      return data;
    } catch (error) {
      throwIfNetworkError(error, 'createEvalPack');
      logError(error, 'createEvalPack');
      throw error;
    }
  },

  // One-time ingest of the pack's inputs through the real pipeline, so evals have documents to
  // replay against. Slow (OCR) — callers should show progress rather than block silently.
  async ingestEvalPack(bundleId, pathway) {
    const url = new URL(`${API_BASE_URL}/api/v1/context/authoring/eval-packs/${encodeURIComponent(bundleId)}/ingest`);
    if (pathway) url.searchParams.append('pathway', pathway);
    const headers = { ...getAuthHeaders() };
    logRequest('POST', url.toString(), headers, null);
    try {
      const response = await fetch(url.toString(), { method: 'POST', headers });
      const data = await response.json();
      logResponse(response, data);
      if (!response.ok) buildAndThrowApiError(response, data, 'Failed to ingest eval pack', 'ingestEvalPack');
      return data;
    } catch (error) {
      throwIfNetworkError(error, 'ingestEvalPack');
      logError(error, 'ingestEvalPack');
      throw error;
    }
  },
};
